<template>
  <div class="content" :class="{ 'marketing-safedig': isSafeDig }">
    <div class="container-fluid mt-4">
      <card class="email-card" v-loading.fullscreen.lock="loading">
        <template slot="header">
          <div class="row align-items-center">
            <div class="col-lg-8 col-md-8 col-12">
              <h2 class="mb-0 text-primary">Email Marketing Hub by Home of Training</h2>
              <p class="text-muted mb-0">Engage learners with targeted, personalized emails in just a few clicks.</p>
            </div>
          </div>
        </template>

        <div class="email-form">
          <!-- Step 1: Select Recipients -->
          <div class="section">
            <h4 class="section-title">Step 1: Choose Your Audience</h4>
            
            <!-- Recipient Type Selection - Card-based Layout -->
            <div class="recipient-selection-container">
              <div class="selection-header">
                <p class="selection-help-text">Select who should receive this email</p>
              </div>
              
              <!-- User Groups Section -->
              <div class="filter-category">
                <div class="category-header">
                  <i class="el-icon-user-solid category-icon"></i>
                  <h5 class="category-title">User Groups</h5>
                  <span class="category-badge">Select one or more</span>
                </div>
                <div class="filter-options-grid">
                  <div
                    v-for="option in groupFilters"
                    :key="option.value"
                    class="filter-option-card"
                    :class="{
                      'selected': selectedFilters.includes(option.value),
                      'disabled': isFilterDisabled(option.value)
                    }"
                    @click="toggleFilter(option.value)"
                  >
                    <div class="filter-card-content">
                      <el-checkbox
                        :value="selectedFilters.includes(option.value)"
                        :disabled="isFilterDisabled(option.value)"
                        @change="toggleFilter(option.value)"
                        @click.native.stop
                      />
                      <div class="filter-card-info">
                        <span class="filter-label">{{ option.label }}</span>
                        <span class="filter-count" v-if="getFilterCount(option.value) !== null && !isCountLoading(option.value)">
                          ({{ getFilterCount(option.value).toLocaleString() }})
                        </span>
                        <span class="filter-count-skeleton" v-if="isCountLoading(option.value)">
                          <i class="el-icon-loading"></i> Loading...
                        </span>
                      </div>
                      <el-tooltip v-if="isFilterDisabled(option.value)" :content="getDisabledReason(option.value)" placement="top">
                        <i class="el-icon-warning-outline filter-warning-icon"></i>
                      </el-tooltip>
                    </div>
                  </div>
                </div>
              </div>

              <!-- User Status Section -->
              <div class="filter-category">
                <div class="category-header">
                  <i class="el-icon-s-flag category-icon"></i>
                  <h5 class="category-title">User Status</h5>
                  <span class="category-badge">Select one or more</span>
                </div>
                <div class="filter-options-grid">
                  <div
                    v-for="option in userStatusFilters"
                    :key="option.value"
                    class="filter-option-card"
                    :class="{
                      'selected': selectedFilters.includes(option.value),
                      'disabled': isFilterDisabled(option.value)
                    }"
                    @click="toggleFilter(option.value)"
                  >
                    <div class="filter-card-content">
                      <el-checkbox
                        :value="selectedFilters.includes(option.value)"
                        :disabled="isFilterDisabled(option.value)"
                        @change="toggleFilter(option.value)"
                        @click.native.stop
                      />
                      <div class="filter-card-info">
                        <span class="filter-label">{{ option.label }}</span>
                        <span class="filter-count" v-if="getFilterCount(option.value) !== null && !isCountLoading(option.value)">
                          ({{ getFilterCount(option.value).toLocaleString() }})
                        </span>
                        <span class="filter-count-skeleton" v-if="isCountLoading(option.value)">
                          <i class="el-icon-loading"></i> Loading...
                        </span>
                      </div>
                      <el-tooltip v-if="isFilterDisabled(option.value)" :content="getDisabledReason(option.value)" placement="top">
                        <i class="el-icon-warning-outline filter-warning-icon"></i>
                      </el-tooltip>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Course Status Section -->
              <div class="filter-category">
                <div class="category-header">
                  <i class="el-icon-document category-icon"></i>
                  <h5 class="category-title">Course Status</h5>
                  <span class="category-badge">Optional filters</span>
                </div>
                <div class="filter-options-grid">
                  <div
                    v-for="option in statusFilters"
                    :key="option.value"
                    class="filter-option-card"
                    :class="{
                      'selected': selectedFilters.includes(option.value),
                      'disabled': isFilterDisabled(option.value)
                    }"
                    @click="toggleFilter(option.value)"
                  >
                    <div class="filter-card-content">
                      <el-checkbox
                        :value="selectedFilters.includes(option.value)"
                        :disabled="isFilterDisabled(option.value)"
                        @change="toggleFilter(option.value)"
                        @click.native.stop
                      />
                      <div class="filter-card-info">
                        <span class="filter-label">{{ option.label }}</span>
                        <span class="filter-count" v-if="getFilterCount(option.value) !== null && !isCountLoading(option.value)">
                          ({{ getFilterCount(option.value).toLocaleString() }})
                        </span>
                        <span class="filter-count-skeleton" v-if="isCountLoading(option.value)">
                          <i class="el-icon-loading"></i> Loading...
                        </span>
                      </div>
                      <el-tooltip v-if="isFilterDisabled(option.value)" :content="getDisabledReason(option.value)" placement="top">
                        <i class="el-icon-warning-outline filter-warning-icon"></i>
                      </el-tooltip>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Validation Error -->
              <div v-if="validationErrors.filters" class="validation-error-banner">
                <i class="el-icon-warning"></i>
                <span>{{ validationErrors.filters }}</span>
              </div>
            </div>

            <!-- Additional Selection Fields (Conditional) -->
            <div class="additional-selections" v-if="selectedFilters.length > 0">
              <!-- Specific Email Input -->
              <div class="selection-field-card" v-if="selectedFilters.includes('specific_emails')">
                <div class="field-header">
                  <i class="el-icon-message field-icon"></i>
                  <label class="field-label">Email Addresses *</label>
                </div>
                <el-input
                  v-model="specificEmail"
                  type="textarea"
                  :rows="3"
                  placeholder="Enter email addresses (one per line or separated by commas)&#10;Example:&#10;user1@example.com&#10;user2@example.com"
                  class="input-primary"
                  :class="{ 'error': validationErrors.specificEmail }"
                  @blur="validateEmails"
                  @input="validateEmails"
                />
                <span v-if="validationErrors.specificEmail" class="error-message">{{ validationErrors.specificEmail }}</span>
                <p class="field-help-text">You can enter multiple emails separated by commas or new lines</p>
              </div>

              <!-- Selected Companies Section -->
              <div class="selection-field-card" v-if="selectedFilters.includes('selected_companies') || selectedFilters.includes('all_companies')">
                <div class="field-header" v-if="selectedFilters.includes('selected_companies')">
                  <i class="el-icon-office-building field-icon"></i>
                  <label class="field-label">Select Companies *</label>
                </div>
                <div v-if="selectedFilters.includes('selected_companies')" class="mb-3">
                  <el-select
                    style="width: 100%"
                    filterable
                    multiple
                    placeholder="Type to search and select companies..."
                    remote
                    reserve-keyword
                    v-model="selectedCompanies"
                    :remote-method="fetchCompanies"
                    :loading="companyLoading"
                    :class="{ 'error': validationErrors.selectedCompanies }"
                    @change="handleSelectedCompaniesChange"
                  >
                    <el-option
                      v-for="option in companiesOption"
                      :key="option.id"
                      :value="option.id"
                      :label="option.name"
                    />
                  </el-select>
                  <span v-if="validationErrors.selectedCompanies" class="error-message">{{ validationErrors.selectedCompanies }}</span>
                </div>
                <div v-if="selectedFilters.includes('all_companies') || selectedFilters.includes('selected_companies')" class="company-filters-card">
                  <label class="company-filters-label">
                    <i class="el-icon-user field-icon-small"></i>
                    Include users from selected companies:
                  </label>
                  <p class="field-help-text company-filters-help">Choose which roles to include. This also limits who appears when you select specific users below.</p>
                  <div class="company-filters">
                    <el-checkbox v-model="companiesFilters.allAdmins">
                      <span class="checkbox-label">Admins</span>
                    </el-checkbox>
                    <el-checkbox v-model="companiesFilters.allManagers">
                      <span class="checkbox-label">Managers</span>
                    </el-checkbox>
                    <el-checkbox v-model="companiesFilters.allEmployees">
                      <span class="checkbox-label">Employees</span>
                    </el-checkbox>
                  </div>
                </div>
                
                <!-- Select specific users: search uses the same role checkboxes above -->
                <div v-if="selectedFilters.includes('selected_companies') && selectedCompanies.length > 0" class="mt-3 company-users-picker">
                  <div class="field-header">
                    <i class="el-icon-user field-icon"></i>
                    <label class="field-label">Select Specific Users (Optional)</label>
                  </div>
                  <p class="field-help-text mb-2">Search by name or email. Results are limited to the roles you checked above (Admins, Managers, Employees).</p>
                  <el-select
                    style="width: 100%"
                    filterable
                    multiple
                    remote
                    reserve-keyword
                    placeholder="Search by name or email..."
                    v-model="companyUsers"
                    :remote-method="fetchUsersFromCompanies"
                    :loading="companyUsersLoading"
                    value-key="id"
                    class="company-users-select"
                    @focus="fetchUsersFromCompanies('')"
                  >
                    <el-option
                      v-for="option in userOptions"
                      :key="option.id"
                      :value="option.id"
                      :label="(option.full_name || option.email || 'User #' + option.id)"
                    />
                  </el-select>
                  <div v-if="companyUsers.length > 0" class="selected-users-summary mt-2">
                    <i class="el-icon-check"></i>
                    <span>{{ companyUsers.length }} {{ companyUsers.length === 1 ? 'user' : 'users' }} selected</span>
                    <el-link type="primary" :underline="false" @click="showSelectedItemsModal('company_users')" class="view-link-btn ml-2">
                      <i class="el-icon-view"></i> View
                    </el-link>
                  </div>
                </div>
              </div>

              <!-- Optional: Narrow to specific users when All Managers / All Employees etc. -->
              <div class="selection-field-card" v-if="hasGroupFilter">
                <div class="field-header">
                  <i class="el-icon-user field-icon"></i>
                  <label class="field-label">Narrow to Specific Users (Optional)</label>
                </div>
                <p class="field-help-text mb-2">Optionally select only certain managers, admins, or employees to target</p>
                <el-select
                  style="width: 100%"
                  filterable
                  multiple
                  remote
                  reserve-keyword
                  placeholder="Type to search by name or email..."
                  v-model="groupSpecificUsers"
                  :remote-method="fetchUsers"
                  :loading="userLoading"
                >
                  <el-option
                    v-for="option in userOptions"
                    :key="option.id"
                    :value="option.id"
                    :label="(option.full_name || option.email || 'User #' + option.id)"
                  />
                </el-select>
                <div v-if="groupSpecificUsers.length > 0" class="selected-users-summary mt-2">
                  <i class="el-icon-check"></i>
                  <span>{{ groupSpecificUsers.length }} {{ groupSpecificUsers.length === 1 ? 'user' : 'users' }} selected</span>
                </div>
              </div>

              <!-- Selected Users Section -->
              <div class="selection-field-card" v-if="selectedFilters.includes('selected_users')">
                <div class="field-header">
                  <i class="el-icon-user field-icon"></i>
                  <label class="field-label">Select Users *</label>
                </div>
                <el-select
                  style="width: 100%"
                  filterable
                  multiple
                  remote
                  reserve-keyword
                  placeholder="Type to search users by name or email..."
                  v-model="selectedUsers"
                  :remote-method="fetchUsers"
                  :loading="userLoading"
                  :class="{ 'error': validationErrors.selectedUsers }"
                  @change="validateField('selectedUsers')"
                >
                  <el-option
                    v-for="option in userOptions"
                    :key="option.id"
                    :value="option.id"
                    :label="option.full_name"
                  />
                </el-select>
                <span v-if="validationErrors.selectedUsers" class="error-message">{{ validationErrors.selectedUsers }}</span>
              </div>
            </div>

            <!-- Course & Certificate Selection - Combined Card -->
            <div class="course-selection-card">
              <div class="course-card-header">
                <div class="course-header-left">
                  <i class="el-icon-reading course-icon"></i>
                  <div>
                    <h5 class="course-card-title">Course & Certificate Filters</h5>
                    <p class="course-card-subtitle">Optionally filter recipients by course and certificate status</p>
                  </div>
                </div>
              </div>
              
              <div class="course-fields-row">
                <div class="course-field">
                  <label class="course-field-label">
                    <i class="el-icon-document"></i>
                    Select Course
                    <span class="optional-badge">Optional</span>
                  </label>
                  <el-select
                    style="width: 100%"
                    filterable
                    remote
                    reserve-keyword
                    placeholder="Search and select a course..."
                    v-model="selectedCourse"
                    :remote-method="fetchCourses"
                    :loading="courseLoading"
                    clearable
                    @change="handleCourseChange"
                  >
                    <el-option
                      v-for="option in courseOptions"
                      :key="option.id"
                      :value="option.id"
                      :label="option.name"
                    />
                  </el-select>
                </div>

                <div class="certificate-field" v-if="selectedCourse">
                  <label class="course-field-label">
                    <i class="el-icon-trophy"></i>
                    Certificate Status
                    <span class="optional-badge">Optional</span>
                  </label>
                  <el-select
                    style="width: 100%"
                    placeholder="Filter by certificate status..."
                    v-model="certificateStatus"
                    clearable
                    @change="handleCertificateStatusChange"
                  >
                    <el-option label="Active Certificates" value="active" />
                    <el-option label="Inactive Certificates" value="inactive" />
                    <el-option label="Expired Certificates" value="expired" />
                  </el-select>
                </div>
              </div>
            </div>

            <!-- Enhanced Recipient Summary -->
            <div class="recipient-summary-card" v-if="recipientSummary || (recipientCount !== 'N/A' && selectedFilters.length > 0)">
              <div class="summary-header">
                <i class="el-icon-info summary-icon"></i>
                <strong class="summary-title">Recipient Summary</strong>
              </div>
              <div class="summary-content">
                <p class="summary-text">
                  <template v-if="recipientSummaryText">{{ recipientSummaryText }}</template>
                  <template v-else-if="recipientCount === 'Calculating...'">Calculating recipients...</template>
                  <template v-else-if="recipientCount !== 'N/A'">
                    {{ recipientCount }} {{ recipientCount.replace(/,/g, '') === '1' ? 'user' : 'users' }}
                  </template>
                  <template v-else>No recipients selected</template>
                </p>
                
                <!-- View Recipients: one link = final list of who will receive the email -->
                <div class="summary-actions">
                  <el-link 
                    v-if="!selectedFilters.includes('specific_emails') && ((recipientSummary && recipientSummary.user_count > 0) || (recipientCount !== 'N/A' && recipientCount > 0))" 
                    type="primary" 
                    :underline="false" 
                    @click="openRecipientsModal" 
                    class="view-link-btn"
                  >
                    <i class="el-icon-view"></i>
                    View Recipients
                  </el-link>
                  
                  <el-link 
                    v-if="selectedFilters.includes('selected_companies') && selectedCompanies.length > 0" 
                    type="primary" 
                    :underline="false" 
                    @click="showSelectedItemsModal('companies')" 
                    class="view-link-btn"
                  >
                    <i class="el-icon-view"></i>
                    View Selected Companies ({{ selectedCompanies.length }})
                  </el-link>
                  
                  <el-link 
                    v-if="selectedFilters.includes('specific_emails') && specificEmail.split(/[\s,]+/).filter(Boolean).length > 0" 
                    type="primary" 
                    :underline="false" 
                    @click="showSelectedItemsModal('emails')" 
                    class="view-link-btn"
                  >
                    <i class="el-icon-view"></i>
                    View Email Addresses ({{ specificEmail.split(/[\s,]+/).filter(Boolean).length }})
                  </el-link>
                </div>
                <p v-if="selectedFilters.includes('selected_users') && selectedUsers.length > 0" class="summary-note">
                  <i class="el-icon-info-outline"></i>
                  Your selection: {{ selectedUsers.length }} {{ selectedUsers.length === 1 ? 'user' : 'users' }}. 
                  <span v-if="selectedCourse || certificateStatus">Recipients above are filtered by course/certificate.</span>
                </p>
              </div>
            </div>

            <!-- Multiple Filters Alert -->
            <div v-if="selectedFilters.length > 1" class="multi-filter-alert">
              <i class="el-icon-info"></i>
              <div class="alert-content">
                <strong>Multiple Filters Selected</strong>
                <p>Targeting users who match <strong>ALL</strong> selected criteria:</p>
                <div class="selected-filters-list">
                  <el-tag v-for="f in selectedFilters" :key="f" size="small" class="filter-tag">
                    {{ getFilterLabel(f) }}
                  </el-tag>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Compose Email -->
          <div class="section">
            <h4 class="section-title">Step 2: Compose Your Email</h4>
            <div class="row mb-4">
              <div class="col-md-8">
                <base-input label="Subject *">
                  <el-input
                    v-model="subject"
                    placeholder="Craft a compelling subject line"
                    class="input-primary"
                    :class="{ 'error': validationErrors.subject }"
                    @blur="validateField('subject')"
                    @input="validateField('subject')"
                  />
                  <span v-if="validationErrors.subject" class="error-message">{{ validationErrors.subject }}</span>
                </base-input>
              </div>
            </div>
            <div class="row mb-4">
              <div class="col-md-8">
                <base-input label="Message *">
                  <vue-editor
                    ref="messageEditor"
                    v-model="content"
                    placeholder="Write your email content here..."
                    :editor-options="editorSettings"
                    :class="{ 'error': validationErrors.content }"
                    @text-change="onTextChange"
                    @blur="validateField('content')"
                  />
                  <span v-if="validationErrors.content" class="error-message">{{ validationErrors.content }}</span>
                </base-input>
              </div>
            </div>

            <div class="row mb-4">
              <div class="col-md-8">
                <base-input label="Email Template">
                  <el-select
                    filterable
                    placeholder="Choose a template (optional)"
                    v-model="selectedTemplate"
                    style="width: 100%"
                    :disabled="selectedFilters.includes('specific_emails')"
                  >
                    <el-option
                      v-for="option in filteredTemplates"
                      :key="option.id"
                      :value="option.id"
                      :label="option.template_name"
                    >
                      <span class="template-label">{{ option.template_name }}</span>
                      <el-tooltip :content="option.description || 'No description available'" placement="top">
                        <i class="el-icon-info template-info"></i>
                      </el-tooltip>
                    </el-option>
                  </el-select>
                </base-input>
              </div>
            </div>
          </div>

          <!-- Step 3: Personalize -->
          <div class="section" :class="{ 'disabled-section': selectedFilters.includes('specific_emails') && selectedFilters.length === 1 }">
            <h4 class="section-title">
              Step 3: Personalize Your Email
              <span v-if="selectedFilters.includes('specific_emails') && selectedFilters.length === 1" class="disabled-text">(Not available for specific emails)</span>
            </h4>
            
            <!-- Search Bar for Placeholders -->
            <div class="placeholder-search-container" v-if="!selectedFilters.includes('specific_emails') || selectedFilters.length > 1">
              <el-input
                v-model="placeholderSearch"
                placeholder="Search placeholders..."
                prefix-icon="el-icon-search"
                clearable
                class="placeholder-search-input"
              />
            </div>

            <!-- Placeholder Categories -->
            <div class="row mb-4" v-if="!selectedFilters.includes('specific_emails') || selectedFilters.length > 1">
              <div class="col-md-12">
                <el-collapse v-model="activeCollapse" accordion>
                  <!-- Essential Placeholders -->
                  <el-collapse-item name="essential">
                    <template slot="title">
                      <span class="collapse-title-text">Essential Placeholders</span>
                    </template>
                    <div class="placeholder-section">
                      <div v-if="filteredEssentialPlaceholders.length === 0" class="no-results">
                        <p>No placeholders found</p>
                      </div>
                      <div v-else class="placeholder-grid">
                        <el-tooltip
                          v-for="p in filteredEssentialPlaceholders"
                          :key="p.value"
                          :content="p.description"
                          placement="top"
                          :disabled="!!placeholderSearch"
                        >
                          <el-tag
                            class="placeholder-tag"
                            :class="{ 'is-disabled': isPlaceholderDisabled(p) }"
                            :disable-transitions="false"
                            :disabled="isPlaceholderDisabled(p)"
                            @click="applyPlaceholder(p.value)"
                          >
                            <span class="placeholder-label">{{ p.label }}</span>
                            <span class="placeholder-value">{{ p.value }}</span>
                          </el-tag>
                        </el-tooltip>
                      </div>
                    </div>
                  </el-collapse-item>

                  <!-- Advanced Placeholders -->
                  <el-collapse-item name="advanced">
                    <template slot="title">
                      <span class="collapse-title-text">Advanced Placeholders</span>
                    </template>
                    <div class="placeholder-section">
                      <div v-if="filteredOtherPlaceholders.length === 0" class="no-results">
                        <p>No placeholders found</p>
                      </div>
                      <div v-else>
                        <!-- Group by category if no search -->
                        <template v-if="!placeholderSearch">
                          <div 
                            v-for="category in placeholderCategories" 
                            :key="category.name"
                            class="placeholder-category"
                          >
                            <div class="category-header-small">
                              <i :class="category.icon"></i>
                              <h6 class="category-name">{{ category.name }}</h6>
                              <span class="category-count">({{ getCategoryPlaceholders(category.filters).length }})</span>
                            </div>
                            <div class="placeholder-grid">
                              <el-tooltip
                                v-for="p in getCategoryPlaceholders(category.filters)"
                                :key="p.value"
                                :content="p.description"
                                placement="top"
                              >
                                <el-tag
                                  class="placeholder-tag"
                                  :class="{ 'is-disabled': isPlaceholderDisabled(p) }"
                                  :disable-transitions="false"
                                  :disabled="isPlaceholderDisabled(p)"
                                  @click="applyPlaceholder(p.value)"
                                >
                                  <span class="placeholder-label">{{ p.label }}</span>
                                  <span class="placeholder-value">{{ p.value }}</span>
                                </el-tag>
                              </el-tooltip>
                            </div>
                          </div>
                        </template>
                        <!-- Flat list when searching -->
                        <div v-else class="placeholder-grid">
                          <el-tooltip
                            v-for="p in filteredOtherPlaceholders"
                            :key="p.value"
                            :content="p.description"
                            placement="top"
                          >
                            <el-tag
                              class="placeholder-tag"
                              :class="{ 'is-disabled': isPlaceholderDisabled(p) }"
                              :disable-transitions="false"
                              :disabled="isPlaceholderDisabled(p)"
                              @click="applyPlaceholder(p.value)"
                            >
                              <span class="placeholder-label">{{ p.label }}</span>
                              <span class="placeholder-value">{{ p.value }}</span>
                            </el-tag>
                          </el-tooltip>
                        </div>
                      </div>
                    </div>
                  </el-collapse-item>
                </el-collapse>
              </div>
            </div>

            <!-- Preview Section -->
            <div class="placeholder-preview" v-if="!selectedFilters.includes('specific_emails') || selectedFilters.length > 1">
              <div class="preview-header">
                <i class="el-icon-view preview-icon"></i>
                <h6 class="preview-title">Quick Preview</h6>
                <el-button 
                  type="text" 
                  size="mini" 
                  @click="showPreviewHelp = !showPreviewHelp"
                  class="preview-help-toggle"
                >
                  {{ showPreviewHelp ? 'Hide' : 'Show' }} Example
                </el-button>
              </div>
              <div class="preview-content">
                <div class="preview-example" v-if="showPreviewHelp">
                  <p class="preview-text">
                    <strong>Example:</strong> "Hi {first_name}, welcome to our platform! Your email is {email}."
                  </p>
                  <p class="preview-result">
                    <strong>Result:</strong> "Hi John, welcome to our platform! Your email is john@example.com."
                  </p>
                </div>
                <div class="preview-tip">
                  <i class="el-icon-light-rain"></i>
                  <span>Tip: Click a placeholder to insert it at your cursor position in the email editor.</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Sticky Action Bar -->
          <div class="action-bar">
            <div class="row">
              <div class="col-md-12 d-flex justify-content-end gap-3">
                <base-button type="default" @click="previewMail" :loading="loading">
                  <i class="el-icon-view mr-1"></i> Preview
                </base-button>
                <base-button type="primary" @click="openSendConfirmModal" :loading="saving">
                  <i class="el-icon-send mr-1"></i> Send Email
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </card>

      <!-- Preview Modal -->
      <modal :show.sync="ifshowPreview" class="email-preview-modal">
        <h3 slot="header" class="modal-title">Email Preview</h3>
        <div class="preview-content">
          <h4>{{ subject || 'No Subject' }}</h4>
          <div class="recipient-info">
            <p><strong>Sample Recipient:</strong> John Doe (john.doe@example.com)</p>
            <p><strong>Total Recipients:</strong> {{ recipientSummary ? recipientSummary.user_count : recipientCount }}</p>
          </div>
          <div v-html="emailHtml" class="email-body"></div>
        </div>
        <template slot="footer">
          <base-button type="default" @click="ifshowPreview = false">Close</base-button>
          <base-button type="primary" @click="openSendConfirmModal" :loading="saving">Send Now</base-button>
        </template>
      </modal>

      <!-- Send confirmation modal: show who will receive, then confirm or dry run -->
      <modal :show.sync="showSendConfirmModal" class="send-confirm-modal">
        <template slot="header">
          <div class="modal-header-content">
            <div class="modal-header-icon">
              <i class="el-icon-message"></i>
            </div>
            <div>
              <h3 class="modal-title">Confirm Send</h3>
              <p class="modal-subtitle">You are about to send emails to the following recipients</p>
            </div>
          </div>
        </template>
        <div class="send-confirm-content">
          <div v-if="confirmLoading" class="confirm-loading">
            <i class="el-icon-loading"></i> Loading recipient list...
          </div>
          <template v-else>
            <p class="confirm-total">
              <strong>{{ confirmRecipientTotal.toLocaleString() }}</strong> {{ confirmRecipientTotal === 1 ? 'recipient' : 'recipients' }} will receive this email.
            </p>
            <p v-if="confirmRecipientSample.length" class="confirm-sample-label">Sample (first {{ confirmRecipientSample.length }}):</p>
            <ul v-if="confirmRecipientSample.length" class="confirm-sample-list">
              <li v-for="(r, idx) in confirmRecipientSample" :key="idx">
                {{ r.name || r.email }} <span class="email-muted">&lt;{{ r.email }}&gt;</span>
              </li>
            </ul>
            <el-checkbox v-model="confirmDryRun" class="confirm-dry-run">
              Dry run (do not send – only log who would receive)
            </el-checkbox>
          </template>
        </div>
        <template slot="footer">
          <base-button type="default" @click="showSendConfirmModal = false">Cancel</base-button>
          <base-button type="primary" @click="confirmAndSend" :loading="saving" :disabled="confirmLoading">
            {{ confirmDryRun ? 'Dry Run (Log Only)' : 'Confirm Send' }}
          </base-button>
        </template>
      </modal>

      <!-- Recipients Modal (for filter-based recipients) -->
      <modal :show.sync="showRecipientsModal" class="selected-items-modal modern-modal">
        <template slot="header">
          <div class="modal-header-content">
            <div class="modal-header-icon">
              <i class="el-icon-user-solid"></i>
            </div>
            <div>
              <h3 class="modal-title">Recipients</h3>
              <p class="modal-subtitle" v-if="!itemsLoading && selectedItemsPagination.total">
                {{ selectedItemsPagination.total.toLocaleString() }} {{ selectedItemsPagination.total === 1 ? 'recipient' : 'recipients' }} found
              </p>
            </div>
          </div>
        </template>
        <div class="selected-items-content">
          <!-- Loading State -->
          <div v-if="itemsLoading" class="modern-loading">
            <div class="loading-spinner">
              <i class="el-icon-loading"></i>
            </div>
            <p class="loading-text">Loading recipients...</p>
          </div>

          <!-- Recipients Table -->
          <div v-else class="table-container">
            <el-table 
              :data="selectedItemsData" 
              style="width: 100%" 
              class="modern-table"
              :empty-text="'No recipients found'"
            >
              <el-table-column prop="name" label="Name" min-width="140" :show-overflow-tooltip="true">
                <template slot-scope="scope">
                  <span class="table-text">{{ scope.row.name || 'N/A' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="email" label="Email" min-width="180" :show-overflow-tooltip="true">
                <template slot-scope="scope">
                  <span class="table-text">{{ scope.row.email || 'N/A' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="role" label="Role" width="120">
                <template slot-scope="scope">
                  <el-tag 
                    :type="getRoleTagType(scope.row.role)" 
                    size="small"
                    class="role-tag"
                  >
                    {{ scope.row.role || 'N/A' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="status" label="Status" width="100">
                <template slot-scope="scope">
                  <el-tag 
                    :type="scope.row.status === 'Active' ? 'success' : 'info'" 
                    size="small"
                    class="status-tag"
                    effect="dark"
                  >
                    {{ scope.row.status || 'N/A' }}
                  </el-tag>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- Pagination -->
          <div v-if="selectedItemsPagination.last_page > 1 && !itemsLoading" class="pagination-container">
            <el-pagination
              @current-change="loadRecipients"
              :current-page="selectedItemsPagination.current_page"
              :page-size="selectedItemsPagination.per_page"
              :total="selectedItemsPagination.total"
              layout="total, prev, pager, next, jumper"
              background
              class="modern-pagination"
            ></el-pagination>
          </div>
        </div>
      </modal>

      <!-- Selected Items Modal -->
      <modal :show.sync="showItemsModal" class="selected-items-modal modern-modal">
        <template slot="header">
          <div class="modal-header-content">
            <div class="modal-header-icon" :class="getModalIconClass(selectedItemsType)">
              <i :class="getModalIcon(selectedItemsType)"></i>
            </div>
            <div>
              <h3 class="modal-title">
                <span v-if="selectedItemsType === 'users'">Selected Users</span>
                <span v-else-if="selectedItemsType === 'company_users'">Selected Users (from Companies)</span>
                <span v-else-if="selectedItemsType === 'companies'">Selected Companies</span>
                <span v-else>Selected Email Addresses</span>
              </h3>
              <p class="modal-subtitle" v-if="!itemsLoading">
                <span v-if="selectedItemsType === 'users'">
                  {{ selectedUsers.length.toLocaleString() }} {{ selectedUsers.length === 1 ? 'user' : 'users' }} selected
                </span>
                <span v-else-if="selectedItemsType === 'company_users'">
                  {{ companyUsers.length.toLocaleString() }} {{ companyUsers.length === 1 ? 'user' : 'users' }} selected
                </span>
                <span v-else-if="selectedItemsType === 'companies'">
                  {{ selectedCompanies.length.toLocaleString() }} {{ selectedCompanies.length === 1 ? 'company' : 'companies' }} selected
                </span>
                <span v-else>
                  {{ specificEmail.split(/[\s,]+/).filter(Boolean).length.toLocaleString() }} {{ specificEmail.split(/[\s,]+/).filter(Boolean).length === 1 ? 'email' : 'emails' }} entered
                </span>
              </p>
            </div>
          </div>
        </template>
        <div class="selected-items-content">
          <!-- Loading State -->
          <div v-if="itemsLoading" class="modern-loading">
            <div class="loading-spinner">
              <i class="el-icon-loading"></i>
            </div>
            <p class="loading-text">Loading...</p>
          </div>

          <!-- Users Table (selected_users or company_users) -->
          <div v-else-if="selectedItemsType === 'users' || selectedItemsType === 'company_users'" class="table-container">
            <el-table 
              :data="selectedItemsData" 
              style="width: 100%" 
              class="modern-table"
              :empty-text="'No users selected'"
            >
              <el-table-column prop="name" label="Name" min-width="140" :show-overflow-tooltip="true">
                <template slot-scope="scope">
                  <span class="table-text">{{ scope.row.name || 'N/A' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="email" label="Email" min-width="180" :show-overflow-tooltip="true">
                <template slot-scope="scope">
                  <span class="table-text">{{ scope.row.email || 'N/A' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="role" label="Role" width="120">
                <template slot-scope="scope">
                  <el-tag 
                    :type="getRoleTagType(scope.row.role)" 
                    size="small"
                    class="role-tag"
                  >
                    {{ scope.row.role || 'N/A' }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="status" label="Status" width="100">
                <template slot-scope="scope">
                  <el-tag 
                    :type="scope.row.status === 'Active' ? 'success' : 'info'" 
                    size="small"
                    class="status-tag"
                    effect="dark"
                  >
                    {{ scope.row.status || 'N/A' }}
                  </el-tag>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- Companies Table -->
          <div v-else-if="selectedItemsType === 'companies'" class="table-container">
            <el-table 
              :data="selectedItemsData" 
              style="width: 100%" 
              class="modern-table"
              :empty-text="'No companies selected'"
            >
              <el-table-column prop="name" label="Company Name" min-width="180" :show-overflow-tooltip="true">
                <template slot-scope="scope">
                  <div class="table-cell-name">
                    <i class="el-icon-office-building table-icon"></i>
                    <span>{{ scope.row.name || 'N/A' }}</span>
                  </div>
                </template>
              </el-table-column>
              <el-table-column prop="is_parent" label="Type" width="140">
                <template slot-scope="scope">
                  <el-tag 
                    :type="scope.row.is_parent ? 'primary' : 'info'" 
                    size="small"
                    class="type-tag"
                    effect="dark"
                  >
                    <i :class="scope.row.is_parent ? 'el-icon-s-home' : 'el-icon-location'"></i>
                    {{ scope.row.is_parent ? 'Parent' : 'Location' }}
                  </el-tag>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- Emails List -->
          <div v-else-if="selectedItemsType === 'emails'" class="email-list-container">
            <div class="email-list-header">
              <i class="el-icon-message"></i>
              <span>Email Addresses</span>
            </div>
            <div class="email-list">
              <el-tag
                v-for="(email, index) in specificEmail.split(/[\s,]+/).filter(Boolean)"
                :key="index"
                class="email-tag-modern"
                closable
                @close="removeEmail(index)"
                effect="plain"
              >
                <i class="el-icon-message email-icon"></i>
                {{ email }}
              </el-tag>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="selectedItemsType !== 'emails' && selectedItemsPagination.last_page > 1 && !itemsLoading" class="pagination-container">
            <el-pagination
              @current-change="loadSelectedItems"
              :current-page="selectedItemsPagination.current_page"
              :page-size="selectedItemsPagination.per_page"
              :total="selectedItemsPagination.total"
              layout="total, prev, pager, next, jumper"
              background
              class="modern-pagination"
            ></el-pagination>
          </div>
        </div>
      </modal>
    </div>
  </div>
</template>

<script>
import { Option, Select, Checkbox, Collapse, CollapseItem, Tooltip, Tag, Alert, Table, TableColumn, Pagination, Link, Input, Button } from 'element-ui';
import { VueEditor } from 'vue2-editor';
import Swal from 'sweetalert2';
import Modal from '@/components/Modal.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseInput from '@/components/Inputs/BaseInput.vue';

export default {
  name: 'EmailSending',
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Checkbox.name]: Checkbox,
    [Collapse.name]: Collapse,
    [CollapseItem.name]: CollapseItem,
    [Tooltip.name]: Tooltip,
    [Tag.name]: Tag,
    [Alert.name]: Alert,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Pagination.name]: Pagination,
    [Link.name]: Link,
    [Input.name]: Input,
    [Button.name]: Button,
    VueEditor,
    Modal,
    BaseButton,
    BaseInput,
  },
  data() {
    return {
      selectedFilters: [],
      specificEmail: '',
      selectedUsers: [],
      selectedCompanies: [],
      companiesFilters: { allAdmins: true, allManagers: true, allEmployees: true },
      selectedCourse: null,
      certificateStatus: null,
      courseOptions: [],
      courseLoading: false,
      recipientSummary: null,
      showItemsModal: false,
      showRecipientsModal: false,
      selectedItemsType: null, // 'users', 'companies', 'emails'
      selectedItemsData: [],
      selectedItemsPagination: {
        current_page: 1,
        per_page: 20,
        total: 0,
        last_page: 1
      },
      itemsLoading: false,
      subject: '',
      content: '',
      selectedTemplate: '',
      manager_count: 0,
      admin_count: 0,
      all_count: 0,
      company_count: 0,
      employee_count: 0,
      individual_count: 0,
      active_users_count: 0,
      inactive_users_count: 0,
      courses_past_due_count: 0,
      course_pending_count: 0,
      compliant_users_count: 0,
      non_compliant_users_count: 0,
      loading: false,
      companyLoading: false,
      userLoading: false,
      saving: false,
      ifshowPreview: false,
      showSendConfirmModal: false,
      confirmRecipientTotal: 0,
      confirmRecipientSample: [],
      confirmDryRun: false,
      confirmLoading: false,
      emailHtml: '',
      emailTemplates: [],
      companiesOption: [],
      userOptions: [],
      validationErrors: {},
      activeCollapse: ['essential'],
      placeholderSearch: '',
      showPreviewHelp: false,
      editorSettings: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image'],
          ],
        },
        theme: 'snow'
      },
      groupFilters: [
        { value: 'all_users', label: 'All Users' },
        { value: 'all_admins', label: 'All Admins' },
        { value: 'all_managers', label: 'All Managers' },
        { value: 'all_employees', label: 'All Employees' },
        { value: 'all_individuals', label: 'All Individuals' },
        { value: 'all_companies', label: 'All Companies' },
        { value: 'selected_users', label: 'Selected Users' },
        { value: 'selected_companies', label: 'Selected Companies' },
        { value: 'specific_emails', label: 'Specific Emails' },
      ],
      userStatusFilters: [
        { value: 'active_users', label: 'Active Users' },
        { value: 'inactive_users', label: 'Inactive Users' },
      ],
      statusFilters: [
        { value: 'courses_past_due', label: 'Courses Past Due' },
        { value: 'course_pending', label: 'Course Pending' },
        { value: 'compliant_users', label: 'Compliant' },
        { value: 'non_compliant_users', label: 'Non-Compliant' },
      ],
      essentialPlaceholders: [
        { value: '{first_name}', label: 'First Name', description: "User's first name", restrictedFilters: [] },
        { value: '{email}', label: 'Email', description: "User's email address", restrictedFilters: [] },
        { value: '{current_date}', label: 'Current Date', description: 'Current date', restrictedFilters: [] },
      ],
      otherPlaceholders: [
        { value: '{last_name}', label: 'Last Name', description: 'User’s last name', restrictedFilters: [] },
        { value: '{full_name}', label: 'Full Name', description: 'User’s full name', restrictedFilters: [] },
        { value: '{phone_number}', label: 'Phone Number', description: 'User’s phone number', restrictedFilters: [] },
        { value: '{joined_date}', label: 'Joined Date', description: 'User’s join date', restrictedFilters: [] },
        { value: '{assigned_courses}', label: 'Assigned Courses', description: 'User’s assigned courses', restrictedFilters: [] },
        { value: '{last_assigned_course}', label: 'Last Assigned Course', description: 'User’s last assigned course', restrictedFilters: [] },
        { value: '{completed_courses}', label: 'Completed Courses', description: 'User’s completed courses', restrictedFilters: [] },
        { value: '{last_completed_course}', label: 'Last Completed Course', description: 'User’s last completed course', restrictedFilters: [] },
        { value: '{employee_courses_passed}', label: 'Passed Courses', description: 'Number of courses passed', restrictedFilters: [] },
        { value: '{employee_courses_failed}', label: 'Failed Courses', description: 'Number of courses failed', restrictedFilters: [] },
        { value: '{employee_courses_open}', label: 'Open Courses', description: 'Number of open courses', restrictedFilters: [] },
        { value: '{employee_courses_overdue}', label: 'Overdue Courses', description: 'Number of overdue courses', restrictedFilters: [] },
        { value: '{expired_courses}', label: 'Expired Courses', description: 'User’s expired courses', restrictedFilters: [] },
        { value: '{course_progress}', label: 'Course Progress', description: 'User’s course progress', restrictedFilters: [] },
        { value: '{total_employees}', label: 'Total Employees', description: 'Total employees managed by admin', restrictedFilters: ['allEmployees', 'allIndividuals'] },
        { value: '{total_courses_passed}', label: 'Total Courses Passed', description: 'Total courses passed by employees', restrictedFilters: ['allEmployees', 'allIndividuals'] },
        { value: '{total_courses_open}', label: 'Total Courses Open', description: 'Total open courses among employees', restrictedFilters: ['allEmployees', 'allIndividuals'] },
        { value: '{total_courses_failed}', label: 'Total Courses Failed', description: 'Total failed courses among employees', restrictedFilters: ['allEmployees', 'allIndividuals'] },
        { value: '{total_courses_overdue}', label: 'Total Courses Overdue', description: 'Total overdue courses among employees', restrictedFilters: ['allEmployees', 'allIndividuals'] },
        { value: '{organization_name}', label: 'Organization Name', description: 'Organization’s name', restrictedFilters: ['allIndividuals'] },
        { value: '{admin_first_name}', label: 'Admin First Name', description: 'Admin’s first name', restrictedFilters: ['allIndividuals'] },
        { value: '{admin_last_name}', label: 'Admin Last Name', description: 'Admin’s last name', restrictedFilters: ['allIndividuals'] },
        { value: '{admin_email}', label: 'Admin Email', description: 'Admin’s email', restrictedFilters: ['allIndividuals'] },
        { value: '{support_email}', label: 'Support Email', description: 'LMS support email', restrictedFilters: [] },
      ],
      nonCombinableFilters: [
        { filter: 'active_users', conflicts: ['inactive_users', 'specific_emails'] },
        { filter: 'inactive_users', conflicts: ['active_users', 'specific_emails'] },
        { filter: 'compliant_users', conflicts: ['non_compliant_users', 'specific_emails'] },
        { filter: 'non_compliant_users', conflicts: ['compliant_users', 'specific_emails'] },
        { filter: 'courses_past_due', conflicts: ['course_pending', 'specific_emails'] },
        { filter: 'course_pending', conflicts: ['courses_past_due', 'specific_emails'] },
        { filter: 'all_users', conflicts: ['specific_emails', 'selected_companies', 'selected_users'] },
        { filter: 'all_admins', conflicts: ['specific_emails', 'selected_companies', 'selected_users'] },
        { filter: 'all_managers', conflicts: ['specific_emails', 'selected_companies', 'selected_users'] },
        { filter: 'all_employees', conflicts: ['specific_emails', 'selected_companies', 'selected_users'] },
        { filter: 'all_individuals', conflicts: ['specific_emails', 'selected_companies', 'selected_users'] },
        { filter: 'all_companies', conflicts: ['specific_emails', 'selected_companies', 'selected_users'] },
        { filter: 'selected_users', conflicts: [
          'all_users', 'all_admins', 'all_managers', 'all_employees', 'all_individuals',
          'all_companies', 'specific_emails'
        ] },
        { filter: 'selected_companies', conflicts: [
          'all_users', 'all_admins', 'all_managers', 'all_employees', 'all_individuals',
          'all_companies', 'specific_emails'
        ] },
        { filter: 'specific_emails', conflicts: [
          'all_users', 'all_admins', 'all_managers', 'all_employees', 'all_individuals',
          'all_companies', 'active_users',
          'inactive_users', 'courses_past_due', 'course_pending', 'compliant_users',
          'non_compliant_users', 'selected_companies', 'selected_users'
        ] },
      ],
      mutuallyExclusiveGroups: [
        ['active_users', 'inactive_users'],
        ['compliant_users', 'non_compliant_users'],
        ['courses_past_due', 'course_pending'],
      ],
      combinedCount: null,
      fetchingCount: false,
      companyUsers: [],
      companyUsersLoading: false,
      groupSpecificUsers: [], // optional narrow-to users when All Managers / All Employees etc. selected
      groupActiveCount: null,
      groupInactiveCount: null,
      groupCoursesPastDueCount: null,
      groupCoursePendingCount: null,
      groupCompliantUsersCount: null,
      groupNonCompliantUsersCount: null,
      countLoading: {},
    };
  },
  computed: {
    isSafeDig() {
      return process.env.VUE_APP_COMPANY_NAME_FRONTEND === 'safedig';
    },
    recipientSummaryText() {
      if (!this.recipientSummary) return '';
      
      const parts = [];
      if (this.recipientSummary.user_count !== undefined) {
        parts.push(`${this.recipientSummary.user_count.toLocaleString()} ${this.recipientSummary.user_count === 1 ? 'user' : 'users'}`);
      }
      
      if (this.recipientSummary.company_count > 0) {
        parts.push(`from ${this.recipientSummary.company_count} ${this.recipientSummary.company_count === 1 ? 'company' : 'companies'}`);
      }
      
      if (this.recipientSummary.course_name) {
        const certStatusText = this.recipientSummary.certificate_status 
          ? ` with ${this.getCertificateStatusLabel(this.recipientSummary.certificate_status)} certificates`
          : '';
        parts.push(`for "${this.recipientSummary.course_name}"${certStatusText}`);
      } else if (this.recipientSummary.certificate_status) {
        parts.push(`with ${this.getCertificateStatusLabel(this.recipientSummary.certificate_status)} certificates`);
      }
      
      return parts.join(' ');
    },
    /** Role param for company user search: derived from Include checkboxes (Admins, Managers, Employees) */
    companyUserRoleParam() {
      const { allAdmins, allManagers, allEmployees } = this.companiesFilters || {};
      const roles = [];
      if (allAdmins) roles.push('admins');
      if (allManagers) roles.push('managers');
      if (allEmployees) roles.push('employees');
      if (roles.length === 0) return 'all';
      if (roles.length === 3) return 'all';
      return roles.join(',');
    },
    hasGroupFilter() {
      const groupFilters = ['all_users', 'all_managers', 'all_admins', 'all_employees', 'all_individuals'];
      return this.selectedFilters.some(f => groupFilters.includes(f));
    },
    recipientCount() {
      if (!this.selectedFilters.length) return 'N/A';

      if (this.selectedFilters.length === 1) {
        const filter = this.selectedFilters[0];
        if (['all_companies', 'selected_companies'].includes(filter)) {
          if (this.fetchingCount) return 'Calculating...';
          return this.combinedCount ? this.combinedCount.toLocaleString() : 'N/A';
        }
        switch (filter) {
          case 'all_users': return this.all_count ? this.all_count.toLocaleString() : 'N/A';
          case 'all_admins': return this.admin_count ? this.admin_count.toLocaleString() : 'N/A';
          case 'all_managers': return this.manager_count ? this.manager_count.toLocaleString() : 'N/A';
          case 'all_employees': return this.employee_count ? this.employee_count.toLocaleString() : 'N/A';
          case 'all_individuals': return this.individual_count ? this.individual_count.toLocaleString() : 'N/A';
          case 'selected_users': 
            if (this.fetchingCount) return 'Calculating...';
            return this.combinedCount !== null ? this.combinedCount.toLocaleString() : 
                   (this.selectedUsers.length ? this.selectedUsers.length.toLocaleString() : 'N/A');
          case 'specific_emails': return this.specificEmail.split(/[\s,]+/).filter(Boolean).length ? this.specificEmail.split(/[\s,]+/).filter(Boolean).length.toLocaleString() : 'N/A';
          case 'active_users': return this.active_users_count ? this.active_users_count.toLocaleString() : 'N/A';
          case 'inactive_users': return this.inactive_users_count ? this.inactive_users_count.toLocaleString() : 'N/A';
          case 'courses_past_due': return this.courses_past_due_count ? this.courses_past_due_count.toLocaleString() : 'N/A';
          case 'course_pending': return this.course_pending_count ? this.course_pending_count.toLocaleString() : 'N/A';
          case 'compliant_users': return this.compliant_users_count ? this.compliant_users_count.toLocaleString() : 'N/A';
          case 'non_compliant_users': return this.non_compliant_users_count ? this.non_compliant_users_count.toLocaleString() : 'N/A';
          default: return 'N/A';
        }
      } else {
        if (this.fetchingCount) return 'Calculating...';
        return this.combinedCount ? this.combinedCount.toLocaleString() : 'N/A';
      }
    },
    canPreview() {
      const contentText = this.content ? this.content.replace(/<[^>]+>/g, '').trim() : '';
      return this.subject.trim() && contentText && this.selectedFilters.length &&
             (!this.selectedFilters.includes('specific_emails') || this.specificEmail.split(/[\s,]+/).filter(Boolean).length > 0) &&
             (!this.selectedFilters.includes('selected_users') || this.selectedUsers.length > 0) &&
             (!this.selectedFilters.includes('selected_companies') || this.selectedCompanies.length > 0);
    },
    filteredTemplates() {
      // If only specific_emails is selected, show only Specific Emails template
      if (this.selectedFilters.includes('specific_emails') && this.selectedFilters.length === 1) {
        return this.emailTemplates.filter(t => t.template_name === 'Specific Emails');
      }
      // If specific_emails is combined with other filters that support placeholders, show standard templates
      if (this.selectedFilters.includes('specific_emails') && 
          (this.selectedFilters.includes('selected_users') || this.selectedFilters.includes('selected_companies'))) {
        return this.emailTemplates.filter(t => t.template_name !== 'Specific Emails');
      }
      // Default: show standard templates
      return this.emailTemplates.filter(t => t.template_name !== 'Specific Emails');
    },
    filteredEssentialPlaceholders() {
      if (!this.placeholderSearch) {
        return this.essentialPlaceholders;
      }
      const search = this.placeholderSearch.toLowerCase();
      return this.essentialPlaceholders.filter(p => 
        p.label.toLowerCase().includes(search) || 
        p.value.toLowerCase().includes(search) ||
        p.description.toLowerCase().includes(search)
      );
    },
    filteredOtherPlaceholders() {
      if (!this.placeholderSearch) {
        return this.otherPlaceholders;
      }
      const search = this.placeholderSearch.toLowerCase();
      return this.otherPlaceholders.filter(p => 
        p.label.toLowerCase().includes(search) || 
        p.value.toLowerCase().includes(search) ||
        p.description.toLowerCase().includes(search)
      );
    },
    placeholderCategories() {
      return [
        {
          name: 'User Information',
          icon: 'el-icon-user',
          filters: (p) => ['first_name', 'last_name', 'full_name', 'email', 'phone_number', 'joined_date'].some(f => p.value.includes(f))
        },
        {
          name: 'Course Information',
          icon: 'el-icon-reading',
          filters: (p) => ['assigned_courses', 'completed_courses', 'expired_courses', 'course_progress', 'last_assigned_course', 'last_completed_course'].some(f => p.value.includes(f))
        },
        {
          name: 'Course Statistics',
          icon: 'el-icon-data-analysis',
          filters: (p) => ['employee_courses_passed', 'employee_courses_failed', 'employee_courses_open', 'employee_courses_overdue'].some(f => p.value.includes(f))
        },
        {
          name: 'Admin & Organization',
          icon: 'el-icon-office-building',
          filters: (p) => ['total_employees', 'total_courses', 'organization_name', 'admin_'].some(f => p.value.includes(f))
        },
        {
          name: 'System & Support',
          icon: 'el-icon-service',
          filters: (p) => ['support_email', 'current_date'].some(f => p.value.includes(f))
        }
      ];
    },
  },
  watch: {
    companiesFilters: {
      handler() {
        if ((this.selectedFilters.includes('all_companies') || this.selectedFilters.includes('selected_companies')) && this.selectedFilters.length) {
          this.fetchRecipientCount();
        }
      },
      deep: true,
    },
    selectedCompanies() {
      if (this.selectedFilters.includes('selected_companies')) {
        this.fetchRecipientCount();
        // Clear company users when companies change
        this.companyUsers = [];
      }
    },
    selectedUsers() {
      if (this.selectedFilters.includes('selected_users')) {
        this.fetchRecipientCount();
      }
    },
    companyUsers() {
      if (this.selectedFilters.includes('selected_companies')) {
        this.fetchRecipientCount();
        this.fetchRecipientSummary();
      }
    },
    groupSpecificUsers: {
      handler() {
        if (this.hasGroupFilter) {
          this.fetchRecipientCount();
          this.fetchRecipientSummary();
        }
      },
      deep: true,
    },
    selectedCourse() {
      this.fetchRecipientCount();
    },
    certificateStatus() {
      this.fetchRecipientCount();
    },
    specificEmail() {
      if (this.selectedFilters.includes('specific_emails')) {
        this.fetchRecipientSummary();
      }
    },
    selectedFilters: {
      handler(newVal, oldVal) {
        // Fetch counts when filters change, especially for combined filters
        if (this.selectedFilters.length > 0) {
          this.fetchRecipientCount();
        }
        // When Active users / Inactive users is toggled with Selected companies, clear selected company users so list matches filter
        if (!this.selectedFilters.includes('selected_companies')) return;
        const oldActive = oldVal && oldVal.includes('active_users');
        const oldInactive = oldVal && oldVal.includes('inactive_users');
        const newActive = this.selectedFilters.includes('active_users');
        const newInactive = this.selectedFilters.includes('inactive_users');
        if (newActive !== oldActive || newInactive !== oldInactive) {
          this.companyUsers = [];
          this.userOptions = [];
        }
      },
      deep: true,
    },
  },
  methods: {
    toggleFilter(filter) {
      const index = this.selectedFilters.indexOf(filter);
      
      // If already selected, allow deselection (don't check disabled state)
      if (index > -1) {
        this.selectedFilters.splice(index, 1);
        this.applyFilters();
        return;
      }
      
      // If not selected, check if it can be selected
      if (this.isFilterDisabled(filter)) return;
      
      // Add the filter
      this.selectedFilters.push(filter);
      this.applyFilters();
    },
    getFilterCount(filter) {
      // When a group filter is selected, User Status and Course Status show counts for that group
      if (this.hasGroupFilter) {
        if (filter === 'active_users' && this.groupActiveCount != null) return this.groupActiveCount;
        if (filter === 'inactive_users' && this.groupInactiveCount != null) return this.groupInactiveCount;
        if (filter === 'courses_past_due' && this.groupCoursesPastDueCount != null) return this.groupCoursesPastDueCount;
        if (filter === 'course_pending' && this.groupCoursePendingCount != null) return this.groupCoursePendingCount;
        if (filter === 'compliant_users' && this.groupCompliantUsersCount != null) return this.groupCompliantUsersCount;
        if (filter === 'non_compliant_users' && this.groupNonCompliantUsersCount != null) return this.groupNonCompliantUsersCount;
      }
      // If we have a combined count and multiple filters are selected, use that
      if (this.selectedFilters.length > 1 && this.combinedCount !== null) {
        // Check if this filter is part of the selected filters
        if (this.selectedFilters.includes(filter)) {
          // For status filters combined with group filters, return combined count
          if (['active_users', 'inactive_users', 'courses_past_due', 'course_pending', 'compliant_users', 'non_compliant_users'].includes(filter)) {
            return this.combinedCount;
          }
        }
      }
      
      switch (filter) {
        case 'all_users': return this.all_count;
        case 'all_admins': return this.admin_count;
        case 'all_managers': return this.manager_count;
        case 'all_employees': return this.employee_count;
        case 'all_individuals': return this.individual_count;
        case 'active_users': return this.active_users_count;
        case 'inactive_users': return this.inactive_users_count;
        case 'courses_past_due': return this.courses_past_due_count;
        case 'course_pending': return this.course_pending_count;
        case 'compliant_users': return this.compliant_users_count;
        case 'non_compliant_users': return this.non_compliant_users_count;
        case 'all_companies': return this.company_count;
        case 'selected_users': return this.selectedUsers.length || null;
        case 'selected_companies': return this.selectedCompanies.length || null;
        case 'specific_emails': return this.specificEmail.split(/[\s,]+/).filter(Boolean).length || null;
        default: return null;
      }
    },
    isCountLoading(filter) {
      return this.countLoading[filter] === true;
    },
    isFilterDisabled(filter) {
      // If filter is already selected, it's never disabled (to allow deselection)
      if (this.selectedFilters.includes(filter)) {
        return false;
      }

      if (!this.selectedFilters.length) return false;

      // Allow selected_users, selected_companies, and specific_emails to be combined with each other
      const combinableFilters = ['specific_emails', 'selected_users', 'selected_companies'];
      const isCombinableFilter = combinableFilters.includes(filter);
      const hasCombinableFilter = this.selectedFilters.some(f => combinableFilters.includes(f));

      // If both the new filter and existing filters are combinable, allow it
      if (isCombinableFilter && hasCombinableFilter) {
        // Check if there are any non-combinable filters selected that would conflict
        const nonCombinableSelected = this.selectedFilters.filter(f => !combinableFilters.includes(f));
        if (nonCombinableSelected.length > 0) {
          // Check if any non-combinable filter conflicts with this combinable filter
          const conflictRule = this.nonCombinableFilters.find(rule => 
            nonCombinableSelected.includes(rule.filter) && rule.conflicts.includes(filter)
          );
          return !!conflictRule;
        }
        return false; // Allow combining combinable filters
      }

      // If trying to add a non-combinable filter when combinable filters are selected
      if (!isCombinableFilter && hasCombinableFilter) {
        // Check if this filter conflicts with any selected combinable filter
        const conflictRule = this.nonCombinableFilters.find(rule => 
          this.selectedFilters.includes(rule.filter) && rule.conflicts.includes(filter)
        );
        return !!conflictRule;
      }

      // Standard conflict checking for non-combinable filters
      const conflictRule = this.nonCombinableFilters.find(rule => 
        this.selectedFilters.includes(rule.filter) && rule.conflicts.includes(filter)
      );

      return !!conflictRule;
    },
    getDisabledReason(filter) {
      const combinableFilters = ['specific_emails', 'selected_users', 'selected_companies'];
      const conflictRule = this.nonCombinableFilters.find(rule =>
        this.selectedFilters.includes(rule.filter) && rule.conflicts.includes(filter)
      );

      if (conflictRule) {
        return `Cannot combine with ${this.getFilterLabel(conflictRule.filter)}`;
      }

      return 'This filter is disabled due to a conflict with selected filters';
    },
    getFilterLabel(filterValue) {
      const groupFilter = this.groupFilters.find(f => f.value === filterValue);
      if (groupFilter) return groupFilter.label;

      const userStatusFilter = this.userStatusFilters.find(f => f.value === filterValue);
      if (userStatusFilter) return userStatusFilter.label;

      const statusFilter = this.statusFilters.find(f => f.value === filterValue);
      if (statusFilter) return statusFilter.label;

      return filterValue;
    },
    isPlaceholderDisabled(placeholder) {
      if (this.selectedFilters.includes('specific_emails') && this.selectedFilters.length === 1) {
        return true;
      }
      if (!placeholder.restrictedFilters || !Array.isArray(placeholder.restrictedFilters)) {
        return false;
      }
      return placeholder.restrictedFilters.some(f => this.selectedFilters.includes(f));
    },
    getCategoryPlaceholders(categoryFilter) {
      return this.otherPlaceholders.filter(p => categoryFilter(p));
    },
    async fetchInitialData() {
      this.loading = true;
      try {
        const [
          managers,
          admins,
          users,
          companies,
          employees,
          individuals,
          activeUsers,
          inactiveUsers,
          coursesPastDue,
          coursePending,
          compliantUsers,
          nonCompliantUsers,
          templates
        ] = await Promise.all([
          this.$http.get('/email-marketing/manager-count').then(resp => resp.data.manager_count).catch(() => 0),
          this.$http.get('/email-marketing/admin-count').then(resp => resp.data.admin_count).catch(() => 0),
          this.$http.get('/email-marketing/all-count').then(resp => resp.data.all_count).catch(() => 0),
          this.$http.get('/email-marketing/company-count').then(resp => resp.data.all_company_count).catch(() => 0),
          this.$http.get('/email-marketing/employee-count').then(resp => resp.data.employee_count).catch(() => 0),
          this.$http.get('/email-marketing/individual-count').then(resp => resp.data.individual_count).catch(() => 0),
          this.$http.get('/email-marketing/active-users-count').then(resp => resp.data.active_users_count).catch(() => 0),
          this.$http.get('/email-marketing/inactive-users-count').then(resp => resp.data.inactive_users_count).catch(() => 0),
          this.$http.get('/email-marketing/courses-past-due-count').then(resp => resp.data.courses_past_due_count).catch(() => 0),
          this.$http.get('/email-marketing/course-pending-count').then(resp => resp.data.course_pending_count).catch(() => 0),
          this.$http.get('/email-marketing/compliant-users-count').then(resp => resp.data.compliant_users_count).catch(() => 0),
          this.$http.get('/email-marketing/non-compliant-users-count').then(resp => resp.data.non_compliant_users_count).catch(() => 0),
          this.$http.get('/email-marketing/marketing-templates').then(resp => resp.data.success ? resp.data.data : []),
        ]);

        this.manager_count = managers;
        this.admin_count = admins;
        this.all_count = users;
        this.company_count = companies;
        this.employee_count = employees;
        this.individual_count = individuals;
        this.active_users_count = activeUsers;
        this.inactive_users_count = inactiveUsers;
        this.courses_past_due_count = coursesPastDue;
        this.course_pending_count = coursePending;
        this.compliant_users_count = compliantUsers;
        this.non_compliant_users_count = nonCompliantUsers;

        this.emailTemplates = templates.map(t => ({
          ...t,
          description: t.description || `Template for ${t.template_name.toLowerCase()} emails`,
        }));

        if (this.emailTemplates.length > 0) {
          const standardTemplate = this.emailTemplates.find(t => t.template_name === 'Standard Template');
          this.selectedTemplate = standardTemplate ? standardTemplate.id : this.emailTemplates[0].id;
        }
      } catch (error) {
        console.error('Failed to load initial data:', error);
        Swal.fire({ title: 'Error', text: 'Failed to load initial data', icon: 'error', confirmButtonColor: '#FE672C' });
      } finally {
        this.loading = false;
      }
    },
    async fetchCompanies(query) {
      if (!query) {
        this.companiesOption = [];
        return;
      }
      this.companyLoading = true;
      try {
        const resp = await this.$http.get('/email-marketing/all-companies', { params: { query } });
        this.companiesOption = resp.data;
      } catch (error) {
        console.error('Failed to fetch companies:', error);
        Swal.fire({ title: 'Error', text: 'Failed to fetch companies', icon: 'error', confirmButtonColor: '#FE672C' });
      } finally {
        this.companyLoading = false;
      }
    },
    async fetchUsers(query) {
      if (!query) {
        this.userOptions = [];
        return;
      }
      this.userLoading = true;
      try {
        const resp = await this.$http.get('/email-marketing/all-users', { params: { query } });
        this.userOptions = resp.data;
      } catch (error) {
        console.error('Failed to fetch users:', error);
        Swal.fire({ title: 'Error', text: 'Failed to fetch users', icon: 'error', confirmButtonColor: '#FE672C' });
      } finally {
        this.userLoading = false;
      }
    },
    async fetchUsersFromCompanies(query) {
      if (!this.selectedCompanies || this.selectedCompanies.length === 0) {
        this.userOptions = [];
        return;
      }
      // Allow empty query to load initial list by role when user focuses the select
      const searchQuery = typeof query === 'string' ? query.trim() : '';
      this.companyUsersLoading = true;
      try {
        // Send companies as comma-separated string so backend receives it reliably (avoids array serialization issues)
        const companiesParam = this.selectedCompanies.join(',');
        const params = {
          query: searchQuery,
          companies: companiesParam,
          role: this.companyUserRoleParam
        };
        // When Active users or Inactive users is selected, only show that status in the list
        if (this.selectedFilters.includes('active_users')) {
          params.status = 1;
        } else if (this.selectedFilters.includes('inactive_users')) {
          params.status = 0;
        }
        const resp = await this.$http.get('/email-marketing/all-users', { params });
        this.userOptions = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
      } catch (error) {
        console.error('Failed to fetch users from companies:', error);
        this.userOptions = [];
      } finally {
        this.companyUsersLoading = false;
      }
    },
    isCountLoading(filter) {
      return this.countLoading[filter] === true;
    },
    async fetchCourses(query) {
      if (!query) {
        this.courseOptions = [];
        return;
      }
      this.courseLoading = true;
      try {
        const resp = await this.$http.get('/email-marketing/all-courses', { params: { query } });
        this.courseOptions = resp.data;
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        Swal.fire({ title: 'Error', text: 'Failed to fetch courses', icon: 'error', confirmButtonColor: '#FE672C' });
      } finally {
        this.courseLoading = false;
      }
    },
    getCertificateStatusLabel(status) {
      const labels = {
        'active': 'active',
        'inactive': 'inactive',
        'expired': 'expired'
      };
      return labels[status] || status;
    },
    handleCourseChange() {
      if (!this.selectedCourse) {
        this.certificateStatus = null;
      }
      this.fetchRecipientCount();
      this.fetchRecipientSummary();
    },
    handleCertificateStatusChange() {
      this.fetchRecipientCount();
      this.fetchRecipientSummary();
    },
    openRecipientsModal() {
      this.showRecipientsModal = true;
      this.selectedItemsPagination.current_page = 1;
      this.loadRecipients(1);
    },
    async loadRecipients(page = 1) {
      this.itemsLoading = true;
      this.selectedItemsPagination.current_page = page;

      try {
        const payload = {
          filters: this.selectedFilters.length ? this.selectedFilters : [],
          page: page,
          per_page: this.selectedItemsPagination.per_page
        };

        if (this.selectedFilters.includes('selected_companies')) {
          payload.companies = this.selectedCompanies;
          if (this.companyUsers && this.companyUsers.length > 0) {
            payload.company_users = this.companyUsers.map(id => ({ id }));
          }
        }
        if (this.selectedFilters.includes('selected_users')) {
          if (this.selectedUsers && this.selectedUsers.length > 0) {
            payload.users = this.selectedUsers.map(id => ({ id }));
          }
        }
        if (this.selectedFilters.includes('specific_emails') && this.specificEmail) {
          payload.emails = this.specificEmail.split(/[\s,]+/).filter(Boolean);
        }
        if (this.selectedFilters.includes('all_companies') || this.selectedFilters.includes('selected_companies')) {
          payload.selectedCompanyFilters = this.companiesFilters;
        }
        if (this.selectedCourse) {
          payload.course_id = this.selectedCourse;
        }
        if (this.certificateStatus) {
          payload.certificate_status = this.certificateStatus;
        }
        if (this.hasGroupFilter && this.groupSpecificUsers && this.groupSpecificUsers.length > 0) {
          payload.group_specific_users = this.groupSpecificUsers;
        }

        const resp = await this.$http.post('/email-marketing/recipients', payload);

        if (resp.data.data) {
          this.selectedItemsData = resp.data.data;
          this.selectedItemsPagination = {
            current_page: resp.data.current_page,
            per_page: resp.data.per_page,
            total: resp.data.total,
            last_page: resp.data.last_page
          };
        }
      } catch (error) {
        console.error('Failed to load recipients:', error);
        this.$message.error('Failed to load recipients');
        this.selectedItemsData = [];
      } finally {
        this.itemsLoading = false;
      }
    },
    showSelectedItemsModal(type) {
      this.selectedItemsType = type;
      this.selectedItemsPagination.current_page = 1;
      this.showItemsModal = true;
      this.loadSelectedItems(1);
    },
    async loadSelectedItems(page = 1) {
      if (this.selectedItemsType === 'emails') {
        // For emails, no pagination needed - show all
        return;
      }

      this.itemsLoading = true;
      this.selectedItemsPagination.current_page = page;

      try {
        let ids = [];
        let apiType = this.selectedItemsType;
        if (this.selectedItemsType === 'users') {
          ids = this.selectedUsers;
        } else if (this.selectedItemsType === 'company_users') {
          ids = this.companyUsers;
          apiType = 'users';
        } else if (this.selectedItemsType === 'companies') {
          ids = this.selectedCompanies;
        }

        if (ids.length === 0) {
          this.selectedItemsData = [];
          this.selectedItemsPagination.total = 0;
          this.itemsLoading = false;
          return;
        }

        const resp = await this.$http.post('/email-marketing/selected-items', {
          type: apiType,
          ids: ids,
          page: page,
          per_page: this.selectedItemsPagination.per_page
        });

        if (resp.data.data) {
          this.selectedItemsData = resp.data.data;
          this.selectedItemsPagination = {
            current_page: resp.data.current_page,
            per_page: resp.data.per_page,
            total: resp.data.total,
            last_page: resp.data.last_page
          };
        }
      } catch (error) {
        console.error('Failed to load selected items:', error);
        this.$message.error('Failed to load selected items');
        this.selectedItemsData = [];
      } finally {
        this.itemsLoading = false;
      }
    },
    removeEmail(index) {
      const emails = this.specificEmail.split(/[\s,]+/).filter(Boolean);
      emails.splice(index, 1);
      this.specificEmail = emails.join(', ');
      this.validateEmails();
    },
    async fetchRecipientSummary() {
      if (!this.selectedFilters.length && !this.selectedCourse) {
        this.recipientSummary = null;
        return;
      }
      
      try {
        // Handle specific_emails - build summary client-side
        if (this.selectedFilters.includes('specific_emails')) {
          const emailCount = this.specificEmail.split(/[\s,]+/).filter(Boolean).length;
          this.recipientSummary = {
            user_count: emailCount,
            company_count: 0,
            course_name: null,
            certificate_status: null,
          };
          return;
        }
        
        // Handle selected_users - build summary client-side
        if (this.selectedFilters.includes('selected_users') && !this.selectedFilters.includes('selected_companies')) {
          // Get course name if applicable
          let courseName = null;
          if (this.selectedCourse) {
            const course = this.courseOptions.find(c => c.id === this.selectedCourse);
            courseName = course ? course.name : null;
          }
          
          this.recipientSummary = {
            user_count: this.combinedCount !== null ? this.combinedCount : this.selectedUsers.length,
            company_count: 0,
            course_name: courseName,
            certificate_status: this.certificateStatus,
          };
          return;
        }

        // When Selected Companies + Select Specific Users: summary = only those selected users (matches actual send)
        if (this.selectedFilters.includes('selected_companies') && this.companyUsers && this.companyUsers.length > 0) {
          let courseName = null;
          if (this.selectedCourse) {
            const course = this.courseOptions.find(c => c.id === this.selectedCourse);
            courseName = course ? course.name : null;
          }
          this.recipientSummary = {
            user_count: this.companyUsers.length,
            company_count: this.selectedCompanies.length,
            course_name: courseName,
            certificate_status: this.certificateStatus,
          };
          return;
        }

        // When group filter (All Managers / All Employees etc.) + Narrow to Specific Users: summary = only those users (matches actual send)
        if (this.hasGroupFilter && this.groupSpecificUsers && this.groupSpecificUsers.length > 0) {
          let courseName = null;
          if (this.selectedCourse) {
            const course = this.courseOptions.find(c => c.id === this.selectedCourse);
            courseName = course ? course.name : null;
          }
          this.recipientSummary = {
            user_count: this.groupSpecificUsers.length,
            company_count: 0,
            course_name: courseName,
            certificate_status: this.certificateStatus,
          };
          return;
        }
        
        // For all other cases, fetch from backend
        const payload = {
          filters: this.selectedFilters,
        };
        
        if (this.selectedFilters.includes('selected_companies')) {
          payload.companies = this.selectedCompanies;
          if (this.companyUsers && this.companyUsers.length > 0) {
            payload.company_users = this.companyUsers.map(id => ({ id }));
          }
        }
        if (this.selectedFilters.includes('all_companies') || this.selectedFilters.includes('selected_companies')) {
          payload.selectedCompanyFilters = this.companiesFilters;
        }
        if (this.selectedCourse) {
          payload.course_id = this.selectedCourse;
        }
        if (this.certificateStatus) {
          payload.certificate_status = this.certificateStatus;
        }
        
        const resp = await this.$http.post('/email-marketing/recipient-summary', payload);
        if (resp.data.error) {
          console.error('Backend error in summary:', resp.data.error);
          this.recipientSummary = null;
        } else {
          this.recipientSummary = resp.data;
        }
      } catch (error) {
        console.error('Failed to fetch recipient summary:', error);
        this.recipientSummary = null;
      }
    },
    applyFilters() {
      const combinableFilters = ['specific_emails', 'selected_users', 'selected_companies'];

      // Handle mutually exclusive groups
      this.mutuallyExclusiveGroups.forEach(group => {
        const selectedInGroup = this.selectedFilters.filter(f => group.includes(f));
        if (selectedInGroup.length > 1) {
          const last = selectedInGroup[selectedInGroup.length - 1];
          this.selectedFilters = this.selectedFilters.filter(f => !group.includes(f) || f === last);
          Swal.fire({
            title: 'Info',
            text: `Only one from "${group.map(this.getFilterLabel).join(' or ')}" can be selected. Kept "${this.getFilterLabel(last)}".`,
            icon: 'info',
            confirmButtonColor: '#FE672C'
          });
        }
      });

      // Check for conflicts between combinable filters and non-combinable filters
      const hasCombinableFilter = this.selectedFilters.some(f => combinableFilters.includes(f));
      const nonCombinableSelected = this.selectedFilters.filter(f => !combinableFilters.includes(f));
      
      if (hasCombinableFilter && nonCombinableSelected.length > 0) {
        // Check if any non-combinable filter conflicts with combinable filters
        const conflictingFilters = [];
        this.selectedFilters.forEach(filter => {
          if (combinableFilters.includes(filter)) {
            const conflictRule = this.nonCombinableFilters.find(rule => 
              nonCombinableSelected.includes(rule.filter) && rule.conflicts.includes(filter)
            );
            if (conflictRule) {
              conflictingFilters.push({
                combinable: filter,
                conflicting: conflictRule.filter
              });
            }
          }
        });

        if (conflictingFilters.length > 0) {
          // Remove conflicting non-combinable filters
          const filtersToRemove = conflictingFilters.map(cf => cf.conflicting);
          this.selectedFilters = this.selectedFilters.filter(f => !filtersToRemove.includes(f));
          Swal.fire({
            title: 'Warning',
            text: `Some filters cannot be combined with ${conflictingFilters.map(cf => this.getFilterLabel(cf.combinable)).join(', ')}. Conflicting filters have been removed.`,
            icon: 'warning',
            confirmButtonColor: '#FE672C'
          });
        }
      }

      // Clear related inputs when filters change
      if (!this.selectedFilters.includes('specific_emails')) {
        this.specificEmail = '';
      }
      if (!this.selectedFilters.includes('selected_users')) {
        this.selectedUsers = [];
      }
      if (!this.selectedFilters.includes('selected_companies')) {
        this.selectedCompanies = [];
      }
      if (!this.hasGroupFilter) {
        this.groupSpecificUsers = [];
        this.groupActiveCount = null;
        this.groupInactiveCount = null;
        this.groupCoursesPastDueCount = null;
        this.groupCoursePendingCount = null;
        this.groupCompliantUsersCount = null;
        this.groupNonCompliantUsersCount = null;
      }

      // Reset company filters to default if not relevant
      if (!this.selectedFilters.includes('all_companies') && !this.selectedFilters.includes('selected_companies')) {
        this.companiesFilters = { allAdmins: true, allManagers: true, allEmployees: true };
      }

      // Clear validation errors
      this.validationErrors = {};

      // Handle template selection and content reset
      if (this.selectedFilters.includes('specific_emails') && this.selectedFilters.length === 1) {
        this.content = '';
        this.$nextTick(() => {
          const editor = this.$refs.messageEditor;
          if (editor && editor.quill) {
            editor.quill.setText('');
          }
        });
        const specificTemplate = this.emailTemplates.find(t => t.template_name === 'Specific Emails');
        if (specificTemplate) {
          this.selectedTemplate = specificTemplate.id;
        } else {
          Swal.fire({
            title: 'Warning',
            text: 'Specific Email template not found. Please contact support.',
            icon: 'warning',
            confirmButtonColor: '#FE672C'
          });
        }
      } else {
        const standardTemplate = this.emailTemplates.find(t => t.template_name === 'Standard Template');
        if (standardTemplate) {
          this.selectedTemplate = standardTemplate.id;
        } else if (this.emailTemplates.length > 0) {
          this.selectedTemplate = this.emailTemplates[0].id;
        }
      }

      // Validate all relevant fields
      this.validateField('filters');
      this.validateField('selectedUsers');
      this.validateField('selectedCompanies');
      this.validateField('specificEmail');

      // Fetch count if applicable
      this.combinedCount = null;
      
      // Always fetch count when filters change or course is selected
      if (this.selectedFilters.length > 0 || this.selectedCourse) {
        this.fetchRecipientCount();
      }
    },
    handleSelectedCompaniesChange() {
      this.validateField('selectedCompanies');
      if (this.selectedFilters.length === 1 && this.selectedFilters[0] === 'selected_companies') {
        this.fetchRecipientCount();
      }
    },
    async fetchRecipientCount() {
      // Set loading states for all selected filters
      this.selectedFilters.forEach(filter => {
        this.$set(this.countLoading, filter, true);
      });
      
      // Handle selected_users separately - use direct count
      if (this.selectedFilters.length === 1 && this.selectedFilters[0] === 'selected_users') {
        if (this.selectedUsers.length > 0) {
          this.combinedCount = this.selectedUsers.length;
          if (this.selectedCourse || this.certificateStatus) {
            // Need to fetch actual count with course/certificate filters
            this.fetchingCount = true;
            try {
              const payload = {
                filters: ['selected_users'],
                selected_users: this.selectedUsers,
              };
              if (this.selectedCourse) {
                payload.course_id = this.selectedCourse;
              }
              if (this.certificateStatus) {
                payload.certificate_status = this.certificateStatus;
              }
              const resp = await this.$http.post('/email-marketing/combined-filters-count', payload);
              this.combinedCount = resp.data.combined_count || this.selectedUsers.length;
              this.fetchRecipientSummary();
            } catch (error) {
              console.error('Failed to fetch recipient count:', error);
              this.combinedCount = this.selectedUsers.length;
            } finally {
              this.fetchingCount = false;
              this.$set(this.countLoading, 'selected_users', false);
            }
          } else {
            this.$set(this.countLoading, 'selected_users', false);
            this.fetchRecipientSummary();
          }
        } else {
          this.combinedCount = 0;
          this.$set(this.countLoading, 'selected_users', false);
        }
        return;
      }

      // Handle specific_emails separately
      if (this.selectedFilters.length === 1 && this.selectedFilters[0] === 'specific_emails') {
        const emails = this.specificEmail.split(/[\s,]+/).filter(Boolean);
        this.combinedCount = emails.length;
        this.$set(this.countLoading, 'specific_emails', false);
        this.fetchRecipientSummary();
        return;
      }

      // When Selected Companies (with or without active_users/inactive_users) and user narrowed to specific users: use narrow count (matches actual send)
      if (this.selectedFilters.includes('selected_companies') && this.companyUsers && this.companyUsers.length > 0) {
        this.combinedCount = this.companyUsers.length;
        this.selectedFilters.forEach(f => this.$set(this.countLoading, f, false));
        this.fetchingCount = false;
        this.fetchRecipientSummary();
        return;
      }

      // Handle all other cases - including combined filters
      if (!this.selectedFilters.length && !this.selectedCourse) {
        this.combinedCount = null;
        Object.keys(this.countLoading).forEach(key => {
          this.$set(this.countLoading, key, false);
        });
        return;
      }

      this.fetchingCount = true;
      this.combinedCount = null;
      const payload = {
        filters: this.selectedFilters.length ? this.selectedFilters : [],
      };
      
      if (this.selectedFilters.includes('selected_companies')) {
        payload.companies = this.selectedCompanies;
      }
      if (this.selectedFilters.includes('all_companies') || this.selectedFilters.includes('selected_companies')) {
        payload.selectedCompanyFilters = this.companiesFilters;
      }
      if (this.selectedFilters.includes('selected_users')) {
        payload.selected_users = this.selectedUsers;
      }
      if (this.hasGroupFilter && this.groupSpecificUsers && this.groupSpecificUsers.length > 0) {
        payload.group_specific_users = this.groupSpecificUsers;
      }
      if (this.selectedCourse) {
        payload.course_id = this.selectedCourse;
      }
      if (this.certificateStatus) {
        payload.certificate_status = this.certificateStatus;
      }
      
      try {
        const resp = await this.$http.post('/email-marketing/combined-filters-count', payload);
        if (resp.data.error) {
          console.error('Backend error:', resp.data.error);
          this.$message.error(resp.data.error);
          this.combinedCount = null;
        } else {
          this.combinedCount = resp.data.combined_count;
          this.groupActiveCount = resp.data.hasOwnProperty('active_count') ? resp.data.active_count : null;
          this.groupInactiveCount = resp.data.hasOwnProperty('inactive_count') ? resp.data.inactive_count : null;
          this.groupCoursesPastDueCount = resp.data.hasOwnProperty('courses_past_due_count') ? resp.data.courses_past_due_count : null;
          this.groupCoursePendingCount = resp.data.hasOwnProperty('course_pending_count') ? resp.data.course_pending_count : null;
          this.groupCompliantUsersCount = resp.data.hasOwnProperty('compliant_users_count') ? resp.data.compliant_users_count : null;
          this.groupNonCompliantUsersCount = resp.data.hasOwnProperty('non_compliant_users_count') ? resp.data.non_compliant_users_count : null;
          this.fetchRecipientSummary();
        }
      } catch (error) {
        console.error('Failed to fetch recipient count:', error);
        const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to fetch recipient count';
        this.$message.error(errorMsg);
        this.combinedCount = null;
      } finally {
        this.fetchingCount = false;
        // Clear loading states for all filters
        this.selectedFilters.forEach(filter => {
          this.$set(this.countLoading, filter, false);
        });
      }
    },
    validateField(field) {
      if (field === 'subject') {
        if (!this.subject || this.subject.trim() === '') {
          this.$set(this.validationErrors, 'subject', 'Subject is required');
        } else {
          this.$delete(this.validationErrors, 'subject');
        }
      } else if (field === 'content') {
        const contentText = this.content ? this.content.replace(/<[^>]+>/g, '').trim() : '';
        if (!contentText) {
          this.$set(this.validationErrors, 'content', 'Message content is required');
        } else {
          this.$delete(this.validationErrors, 'content');
        }
      } else if (field === 'filters') {
        if (!this.selectedFilters.length) {
          this.$set(this.validationErrors, 'filters', 'Please select at least one recipient filter');
        } else {
          this.$delete(this.validationErrors, 'filters');
        }
      } else if (field === 'selectedUsers' && this.selectedFilters.includes('selected_users')) {
        if (!this.selectedUsers.length) {
          this.$set(this.validationErrors, 'selectedUsers', 'Please select at least one user');
        } else {
          this.$delete(this.validationErrors, 'selectedUsers');
        }
      } else if (field === 'selectedCompanies' && this.selectedFilters.includes('selected_companies')) {
        if (!this.selectedCompanies.length) {
          this.$set(this.validationErrors, 'selectedCompanies', 'Please select at least one company');
        } else {
          this.$delete(this.validationErrors, 'selectedCompanies');
        }
      } else if (field === 'specificEmail' && this.selectedFilters.includes('specific_emails')) {
        this.validateEmails();
      }
    },
    validateEmails() {
      if (!this.selectedFilters.includes('specific_emails')) {
        this.$delete(this.validationErrors, 'specificEmail');
        return;
      }
      const emails = this.specificEmail.split(/[\s,]+/).filter(Boolean);
      if (!emails.length) {
        this.$set(this.validationErrors, 'specificEmail', 'At least one email address is required');
      } else {
        const emailRegex = /^\S+@\S+\.\S+$/;
        const invalidEmails = emails.filter(email => !emailRegex.test(email));
        if (invalidEmails.length) {
          this.$set(this.validationErrors, 'specificEmail', `Invalid emails: ${invalidEmails.join(', ')}`);
        } else {
          this.$delete(this.validationErrors, 'specificEmail');
        }
      }
    },
    getFirstValidationError() {
      if (!this.selectedFilters.length) {
        return 'Please select at least one recipient filter';
      }
      if (this.selectedFilters.includes('specific_emails')) {
        const emails = this.specificEmail.split(/[\s,]+/).filter(Boolean);
        if (!emails.length) {
          return 'Please enter at least one email address';
        } else {
          const emailRegex = /^\S+@\S+\.\S+$/;
          const invalidEmails = emails.filter(email => !emailRegex.test(email));
          if (invalidEmails.length) {
            return `Invalid email format: ${invalidEmails[0]}`;
          }
        }
      }
      if (this.selectedFilters.includes('selected_users') && !this.selectedUsers.length) {
        return 'Please select at least one user';
      }
      if (this.selectedFilters.includes('selected_companies') && !this.selectedCompanies.length) {
        return 'Please select at least one company';
      }
      if (!this.subject || this.subject.trim() === '') {
        return 'Please enter an email subject';
      }
      const contentText = this.content ? this.content.replace(/<[^>]+>/g, '').trim() : '';
      if (!contentText) {
        return 'Please enter your email message';
      }
      return null;
    },
    validateForm() {
      this.validateField('subject');
      this.validateField('content');
      this.validateField('filters');

      // Validate inputs for exclusive filters
      if (this.selectedFilters.includes('specific_emails')) {
        this.validateEmails();
      }
      if (this.selectedFilters.includes('selected_users')) {
        this.validateField('selectedUsers');
      }
      if (this.selectedFilters.includes('selected_companies')) {
        this.validateField('selectedCompanies');
      }

      return Object.keys(this.validationErrors).length === 0;
    },
    onTextChange() {
      this.$nextTick(() => {
        this.validateField('content');
      });
    },
    applyPlaceholder(placeholder) {
      if (this.selectedFilters.includes('specific_emails') && this.selectedFilters.length === 1) return;
      const editor = this.$refs.messageEditor;
      if (!editor || !editor.quill) {
        console.warn('Editor not ready, adding placeholder to content directly');
        this.content = (this.content || '') + placeholder;
        this.validateField('content');
        this.$nextTick(() => {
          const retryEditor = this.$refs.messageEditor;
          if (retryEditor && retryEditor.quill) {
            retryEditor.quill.root.innerHTML = this.content;
          }
        });
        return;
      }
      try {
        const quill = editor.quill;
        const range = quill.getSelection(true) || { index: quill.getLength() };
        quill.insertText(range.index, placeholder);
        quill.setSelection(range.index + placeholder.length);
        this.content = quill.root.innerHTML;
        this.validateField('content');
      } catch (error) {
        console.error('Error inserting placeholder:', error);
        this.content = (this.content || '') + placeholder;
        this.validateField('content');
      }
    },
    copyPlaceholder(placeholder) {
      navigator.clipboard.writeText(placeholder).then(() => {
        Swal.fire({
          title: 'Copied!',
          text: `Placeholder ${placeholder} copied to clipboard`,
          icon: 'success',
          confirmButtonColor: '#FE672C',
          timer: 1500,
          showConfirmButton: false,
        });
      }).catch(error => {
        console.error('Failed to copy placeholder:', error);
        Swal.fire({ title: 'Error', text: 'Failed to copy placeholder', icon: 'error', confirmButtonColor: '#FE672C' });
      });
    },
    async previewMail() {
      const firstError = this.getFirstValidationError();
      if (firstError) {
        Swal.fire({ 
          title: 'Required Field Missing', 
          text: firstError, 
          icon: 'warning', 
          confirmButtonColor: '#FE672C' 
        });
        return;
      }
      this.loading = true;
      try {
        const editor = this.$refs.messageEditor;
        let currentContent = this.content;
        if (editor && editor.quill) {
          currentContent = editor.quill.root.innerHTML;
          this.content = currentContent;
        }
        const data = {
          subject: this.subject,
          body: currentContent,
          template: this.selectedTemplate || null,
        };
        console.log('Preview request:', data);
        const response = await this.$http.post('/email-marketing/email-preview', { data });
        console.log('Preview response:', response.data);
        if (response.data.html) {
          this.emailHtml = response.data.html.replace('{unsubscribe_link}', '/unsubscribe?token={token}');
          this.ifshowPreview = true;
        } else {
          throw new Error('Invalid preview response: No HTML content');
        }
      } catch (error) {
        console.error('Preview error:', error);
        Swal.fire({ 
          title: 'Preview Error', 
          text: error.response?.data?.message || 'Failed to load preview. Please try again.', 
          icon: 'error', 
          confirmButtonColor: '#FE672C' 
        });
      } finally {
        this.loading = false;
      }
    },
    buildRecipientPreviewPayload() {
      const payload = {
        filters: this.selectedFilters.length ? this.selectedFilters : [],
        page: 1,
        per_page: 15
      };
      if (this.selectedFilters.includes('selected_companies')) {
        payload.companies = this.selectedCompanies;
        if (this.companyUsers && this.companyUsers.length > 0) {
          payload.company_users = this.companyUsers.map(id => ({ id }));
        }
      }
      if (this.selectedFilters.includes('selected_users')) {
        if (this.selectedUsers && this.selectedUsers.length > 0) {
          payload.users = this.selectedUsers.map(id => ({ id }));
        }
      }
      if (this.selectedFilters.includes('specific_emails') && this.specificEmail) {
        payload.emails = this.specificEmail.split(/[\s,]+/).filter(Boolean);
      }
      if (this.selectedFilters.includes('all_companies') || this.selectedFilters.includes('selected_companies')) {
        payload.selectedCompanyFilters = this.companiesFilters;
      }
      if (this.selectedCourse) payload.course_id = this.selectedCourse;
      if (this.certificateStatus) payload.certificate_status = this.certificateStatus;
      if (this.hasGroupFilter && this.groupSpecificUsers && this.groupSpecificUsers.length > 0) {
        payload.group_specific_users = this.groupSpecificUsers;
      }
      return payload;
    },
    async openSendConfirmModal() {
      const firstError = this.getFirstValidationError();
      if (firstError) {
        Swal.fire({
          title: 'Required Field Missing',
          text: firstError,
          icon: 'warning',
          confirmButtonColor: '#FE672C'
        });
        return;
      }
      this.showSendConfirmModal = true;
      this.confirmRecipientTotal = 0;
      this.confirmRecipientSample = [];
      this.confirmDryRun = false;
      this.confirmLoading = true;
      try {
        const payload = this.buildRecipientPreviewPayload();
        const resp = await this.$http.post('/email-marketing/recipients', payload);
        if (resp.data.data) {
          // When narrowed to specific company users, show that count so it matches actual send
          if (this.selectedFilters.includes('selected_companies') && this.companyUsers && this.companyUsers.length > 0) {
            this.confirmRecipientTotal = this.companyUsers.length;
          } else {
            this.confirmRecipientTotal = resp.data.total ?? 0;
          }
          this.confirmRecipientSample = resp.data.data || [];
        }
      } catch (error) {
        console.error('Failed to load recipient preview:', error);
        this.$message.error('Failed to load recipient list');
        this.showSendConfirmModal = false;
      } finally {
        this.confirmLoading = false;
      }
    },
    confirmAndSend() {
      this.sendMail(this.confirmDryRun);
      this.showSendConfirmModal = false;
    },
    async sendMail(dryRun = false) {
      const firstError = this.getFirstValidationError();
      if (firstError) {
        Swal.fire({
          title: 'Required Field Missing',
          text: firstError,
          icon: 'warning',
          confirmButtonColor: '#FE672C'
        });
        return;
      }

      this.saving = true;
      try {
        const editor = this.$refs.messageEditor;
        let currentContent = this.content;
        if (editor && editor.quill) {
          currentContent = editor.quill.root.innerHTML;
          this.content = currentContent;
        }

        let endpoint;
        let data = {
          subject: this.subject,
          body: currentContent,
          template: this.selectedTemplate || null,
          dry_run: !!dryRun,
        };

        // Add course and certificate filters if selected
        if (this.selectedCourse) {
          data.course_id = this.selectedCourse;
        }
        if (this.certificateStatus) {
          data.certificate_status = this.certificateStatus;
        }

        // When a single group filter (All users, All managers, etc.) has any narrowing (specific users, course, or certificate),
        // use the combined endpoint so the backend applies all narrowings. Otherwise we'd send to everyone.
        const singleFilterWithNarrowing = this.selectedFilters.length === 1 &&
          this.hasGroupFilter &&
          (this.groupSpecificUsers?.length > 0 || this.selectedCourse || this.certificateStatus);

        if (this.selectedFilters.length === 1 && !singleFilterWithNarrowing) {
          const filter = this.selectedFilters[0];
          endpoint = this.getEndpoint(filter);
          if (filter === 'specific_emails') {
            data.emails = this.specificEmail.split(/[\s,]+/).filter(Boolean);
          } else if (filter === 'selected_users') {
            data.users = this.selectedUsers.map(id => ({ id }));
          } else if (filter === 'selected_companies') {
            data.companies = this.selectedCompanies;
            data.selectedCompanyFilters = this.companiesFilters;
            // Narrow to specific users within selected companies (sent so backend uses only these recipients)
            if (this.companyUsers && this.companyUsers.length > 0) {
              data.company_users = this.companyUsers.map(id => ({ id }));
            }
          } else if (filter === 'all_companies') {
            data.selectedCompanyFilters = this.companiesFilters;
          } else if (this.hasGroupFilter && this.groupSpecificUsers && this.groupSpecificUsers.length > 0) {
            data.group_specific_users = this.groupSpecificUsers;
          }
        } else if (singleFilterWithNarrowing || this.selectedFilters.length > 1) {
          endpoint = '/email-marketing/send-email-combined';
          data.filters = this.selectedFilters;
          
          // Include data for combinable filters
          if (this.selectedFilters.includes('specific_emails')) {
            data.emails = this.specificEmail.split(/[\s,]+/).filter(Boolean);
          }
          if (this.selectedFilters.includes('selected_users')) {
            data.users = this.selectedUsers.map(id => ({ id }));
          }
          if (this.selectedFilters.includes('selected_companies')) {
            data.companies = this.selectedCompanies;
            data.selectedCompanyFilters = this.companiesFilters;
            // Narrow to specific users within selected companies (separate key so selected_users list is not overwritten)
            if (this.companyUsers && this.companyUsers.length > 0) {
              data.company_users = this.companyUsers.map(id => ({ id }));
            }
          }
          if (this.selectedFilters.includes('all_companies')) {
            data.selectedCompanyFilters = this.companiesFilters;
          }
          if (this.hasGroupFilter && this.groupSpecificUsers && this.groupSpecificUsers.length > 0) {
            data.group_specific_users = this.groupSpecificUsers;
          }
        }

        console.log('Send request:', { endpoint, data });
        const response = await this.$http.post(endpoint, { data });
        console.log('Send response:', response.data);

        const isDryRun = !!dryRun;
        Swal.fire({
          title: isDryRun ? 'Dry Run Complete' : 'Success',
          text: isDryRun
            ? (response.data.success || 'Dry run completed. No emails were sent. Check logs for recipient list.')
            : (response.data.success || 'Email campaign launched successfully!'),
          icon: 'success',
          confirmButtonColor: '#FE672C',
          showConfirmButton: true,
          timer: isDryRun ? 5000 : 3000,
        });

        this.resetFields();
      } catch (error) {
        console.error('Send error:', error);
        this.handleErrors(error);
      } finally {
        this.saving = false;
      }
    },
    getEndpoint(filter) {
      const endpoints = {
        all_users: '/email-marketing/send-email-all-users',
        all_admins: '/email-marketing/send-email-all-admins',
        all_managers: '/email-marketing/send-email-all-managers',
        all_employees: '/email-marketing/send-email-all-employees',
        all_individuals: '/email-marketing/send-email-all-individuals',
        selected_users: '/email-marketing/send-email-selected-users',
        all_companies: '/email-marketing/send-email-all-companies',
        selected_companies: '/email-marketing/send-email-selected-companies',
        specific_emails: '/email-marketing/send-email-specific',
        active_users: '/email-marketing/send-email-active-users',
        inactive_users: '/email-marketing/send-email-inactive-users',
        courses_past_due: '/email-marketing/send-email-courses-past-due',
        course_pending: '/email-marketing/send-email-course-pending',
        compliant_users: '/email-marketing/send-email-compliant',
        non_compliant_users: '/email-marketing/send-email-non-compliant',
      };
      return endpoints[filter] || '/email-marketing/send-email';
    },
    handleErrors(error) {
      if (error.response && error.response.data) {
        if (error.response.data.errors) {
          const errorMessages = Object.values(error.response.data.errors).flat().join('\n');
          Swal.fire({
            title: 'Validation Error',
            text: errorMessages,
            icon: 'error',
            confirmButtonColor: '#FE672C'
          });
        } else if (error.response.data.message) {
          Swal.fire({
            title: 'Error',
            text: error.response.data.message,
            icon: 'error',
            confirmButtonColor: '#FE672C'
          });
        } else {
          Swal.fire({
            title: 'Error',
            text: 'Failed to send email. Please try again.',
            icon: 'error',
            confirmButtonColor: '#FE672C'
          });
        }
      } else {
        Swal.fire({
          title: 'Error',
          text: 'Network error. Please check your connection and try again.',
          icon: 'error',
          confirmButtonColor: '#FE672C'
        });
      }
      console.error('Errors:', error);
    },
    resetFields() {
      this.selectedFilters = [];
      this.specificEmail = '';
      this.selectedUsers = [];
      this.selectedCompanies = [];
      this.companyUsers = [];
      this.groupSpecificUsers = [];
      this.showSendConfirmModal = false;
      this.confirmDryRun = false;
      this.groupActiveCount = null;
      this.groupInactiveCount = null;
      this.groupCoursesPastDueCount = null;
      this.groupCoursePendingCount = null;
      this.groupCompliantUsersCount = null;
      this.groupNonCompliantUsersCount = null;
      this.companiesFilters = { allAdmins: true, allManagers: true, allEmployees: true };
      this.selectedCourse = null;
      this.certificateStatus = null;
      this.courseOptions = [];
      this.recipientSummary = null;
      this.subject = '';
      this.content = '';
      if (this.emailTemplates.length > 0) {
        const standardTemplate = this.emailTemplates.find(t => t.template_name === 'Standard Template');
        this.selectedTemplate = standardTemplate ? standardTemplate.id : this.emailTemplates[0].id;
      }
      this.$nextTick(() => {
        const editor = this.$refs.messageEditor;
        if (editor && editor.quill) {
          editor.quill.setText('');
        }
      });
      this.validationErrors = {};
      this.ifshowPreview = false;
      this.emailHtml = '';
      this.companiesOption = [];
      this.userOptions = [];
      this.combinedCount = null;
      this.fetchingCount = false;
      this.countLoading = {};
    },
    getModalIcon(type) {
      const icons = {
        'users': 'el-icon-user-solid',
        'company_users': 'el-icon-user-solid',
        'companies': 'el-icon-office-building',
        'emails': 'el-icon-message'
      };
      return icons[type] || 'el-icon-document';
    },
    getModalIconClass(type) {
      const classes = {
        'users': 'icon-users',
        'company_users': 'icon-users',
        'companies': 'icon-companies',
        'emails': 'icon-emails'
      };
      return classes[type] || '';
    },
    getRoleTagType(role) {
      const roleMap = {
        'Admin': 'danger',
        'Manager': 'warning',
        'Employee': 'success',
        'Individual': 'info'
      };
      return roleMap[role] || 'info';
    },
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(() => {
        const editor = this.$refs.messageEditor;
        if (editor && editor.quill) {
          console.log('Editor successfully mounted');
          editor.quill.on('text-change', () => {
            this.content = editor.quill.root.innerHTML;
            this.validateField('content');
          });
        } else {
          console.log('Editor not ready, will retry...');
          setTimeout(() => {
            const retryEditor = this.$refs.messageEditor;
            if (retryEditor && retryEditor.quill) {
              console.log('Editor ready on second attempt');
              retryEditor.quill.on('text-change', () => {
                this.content = retryEditor.quill.root.innerHTML;
                this.validateField('content');
              });
            }
          }, 1000);
        }
      }, 500);
    });
  },
  created() {
    this.fetchInitialData();
  }
}
</script>

<style scoped>
.email-card {
  border-radius: 12px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  background: #fff;
  transition: transform 0.3s ease;
}
.email-card:hover {
  transform: translateY(-4px);
}
.section {
  margin-bottom: 32px;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
  transition: all 0.3s ease;
}
.disabled-section {
  opacity: 0.6;
  background: #f0f0f0 !important;
  pointer-events: none;
}
.disabled-text {
  font-size: 14px;
  font-weight: 400;
  color: #999;
  font-style: italic;
  margin-left: 10px;
}
.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #444C57;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}
.section-title::before {
  content: '';
  width: 4px;
  height: 20px;
  background: #FE672C;
  margin-right: 12px;
  border-radius: 2px;
}
/* Placeholder Search */
.placeholder-search-container {
  margin-bottom: 24px;
}

.placeholder-search-input {
  width: 100%;
}

.placeholder-search-input ::v-deep .el-input {
  width: 100%;
}

.placeholder-search-input ::v-deep .el-input__inner {
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  padding: 12px 16px 12px 40px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #fff;
}

.placeholder-search-input ::v-deep .el-input__inner:hover {
  border-color: #c0c4cc;
}

.placeholder-search-input ::v-deep .el-input__inner:focus {
  border-color: #FE672C;
  box-shadow: 0 0 0 2px rgba(254, 103, 44, 0.1);
}

.placeholder-search-input ::v-deep .el-input__prefix {
  left: 12px;
}

.placeholder-search-input ::v-deep .el-input__prefix .el-input__icon {
  color: #909399;
}

/* Collapse Title */
.collapse-title-text {
  font-weight: 600;
  color: #444C57;
  font-size: 15px;
}

/* Placeholder Section */
.placeholder-section {
  padding: 16px 0;
}

.no-results {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.no-results p {
  margin: 0;
  font-size: 14px;
  color: #909399;
}

/* Placeholder Grid */
.placeholder-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

/* Placeholder Tag */
.placeholder-tag {
  cursor: pointer;
  background: #fff;
  color: #444C57;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 13px;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.placeholder-tag:hover:not(.is-disabled) {
  border-color: #FE672C;
  background: #fff5f0;
  box-shadow: 0 2px 8px rgba(254, 103, 44, 0.12);
  transform: translateY(-1px);
}

.placeholder-tag.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f7fa;
  color: #c0c4cc;
  border-color: #e4e7ed;
  pointer-events: none;
  box-shadow: none;
}

.placeholder-label {
  font-weight: 600;
  color: #303133;
  font-size: 13px;
  line-height: 1.4;
}

.placeholder-value {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 11px;
  color: #FE672C;
  background: #fff5f0;
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
  font-weight: 500;
  letter-spacing: 0.3px;
  border: 1px solid #ffe5d6;
}

/* Placeholder Categories */
.placeholder-category {
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid #ebeef5;
}

.placeholder-category:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.category-header-small {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f2f5;
}

.category-header-small i {
  font-size: 18px;
  color: #FE672C;
  background: #fff5f0;
  padding: 6px;
  border-radius: 4px;
}

.category-name {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin: 0;
}

.category-count {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
  margin-left: 6px;
  background: #f5f7fa;
  padding: 2px 8px;
  border-radius: 10px;
}

/* Placeholder Preview */
.placeholder-preview {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  padding: 20px;
  margin-top: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4e7ed;
}

.preview-icon {
  font-size: 20px;
  color: #FE672C;
  background: #fff5f0;
  padding: 8px;
  border-radius: 6px;
}

.preview-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin: 0;
  flex: 1;
}

.preview-help-toggle {
  font-size: 13px;
  color: #fff;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.preview-help-toggle:hover {
  background: #fff5f0;
  color: #FE672C;
}

.preview-content {
  padding-left: 0;
}

.preview-example {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.preview-text {
  margin: 0 0 12px 0;
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
}

.preview-text:last-child {
  margin-bottom: 0;
}

.preview-text strong {
  color: #303133;
  font-weight: 600;
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-result {
  margin: 12px 0 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #67c23a;
  background: #f0f9ff;
  padding: 12px 16px;
  border-radius: 6px;
  border-left: 3px solid #67c23a;
}

.preview-result strong {
  color: #67c23a;
  font-weight: 600;
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.preview-tip {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background: #fff5f0;
  border-radius: 8px;
  border-left: 3px solid #FE672C;
}

.preview-tip i {
  font-size: 18px;
  color: #FE672C;
  margin-top: 2px;
  flex-shrink: 0;
}

.preview-tip span {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  flex: 1;
}

/* Collapse Styling */
::v-deep .el-collapse {
  border: none;
}

::v-deep .el-collapse-item {
  margin-bottom: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

::v-deep .el-collapse-item__header {
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid #f0f2f5;
  transition: all 0.2s ease;
  font-weight: 600;
  color: #303133;
}

::v-deep .el-collapse-item__header:hover {
  background: #fafbfc;
}

::v-deep .el-collapse-item__header.is-active {
  border-bottom: 1px solid #e4e7ed;
}

::v-deep .el-collapse-item__wrap {
  border: none;
}

::v-deep .el-collapse-item__content {
  padding: 20px;
  background: #fafbfc;
  border-radius: 0 0 8px 8px;
}

::v-deep .el-collapse-item__arrow {
  color: #909399;
  font-size: 14px;
}
.company-filters {
  display: flex;
  gap: 20px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.error-message {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 6px;
  display: block;
}
::v-deep .error .el-input__inner,
::v-deep .error .ql-container {
  border-color: #f56c6c !important;
}
::v-deep .el-button--primary {
  background-color: #FE672C;
  border-color: #FE672C;
  border-radius: 6px;
  padding: 12px 24px;
  font-weight: 600;
}
::v-deep .el-button--primary:hover {
  background-color: #ff8a5c;
  border-color: #ff8a5c;
}
::v-deep .el-button--default {
  border-color: #dcdfe6;
  color: #444C57;
  border-radius: 6px;
  padding: 12px 24px;
}
::v-deep .el-input__inner:focus,
::v-deep .el-select .el-input__inner:focus {
  border-color: #FE672C !important;
  box-shadow: 0 0 0 2px rgba(254, 103, 44, 0.2) !important;
}
::v-deep .el-collapse-item__header {
  font-weight: 600;
  color: #444C57;
  background: #fff;
  border-radius: 4px;
  padding-left: 20px;
}
::v-deep .el-collapse-item__content {
  padding: 20px;
  background: #f0f9ff;
  border-radius: 4px;
}
.email-preview-modal ::v-deep .modal-dialog {
  max-width: 80vw;
  width: 80vw;
  border-radius: 12px;
  margin: 1.75rem auto;
}
.email-preview-modal ::v-deep .modal-content {
  width: 100%;
}
.preview-content {
  padding: 24px;
  background: #fff;
  border-radius: 8px;
}
.email-body {
  border: 1px solid #e4e7ed;
  padding: 20px;
  border-radius: 6px;
  min-height: 300px;
  overflow-y: auto;
}
.recipient-info {
  margin-bottom: 20px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}
.send-confirm-modal .send-confirm-content {
  padding: 16px 0;
}
.send-confirm-modal .confirm-loading {
  padding: 24px;
  text-align: center;
  color: #606266;
}
.send-confirm-modal .confirm-total {
  margin-bottom: 12px;
  font-size: 15px;
}
.send-confirm-modal .confirm-sample-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 8px;
}
.send-confirm-modal .confirm-sample-list {
  max-height: 200px;
  overflow-y: auto;
  padding-left: 20px;
  margin-bottom: 16px;
  font-size: 13px;
}
.send-confirm-modal .confirm-sample-list li {
  margin-bottom: 4px;
}
.send-confirm-modal .email-muted {
  color: #909399;
  font-size: 12px;
}
.send-confirm-modal .confirm-dry-run {
  display: block;
  margin-top: 12px;
}
.template-label {
  flex: 1;
}
.template-info {
  color: #FE672C;
  margin-left: 10px;
}
.action-bar {
  position: sticky;
  bottom: 0;
  background: #fff;
  padding: 16px 24px;
  border-top: 1px solid #e4e7ed;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
  z-index: 10;
}
.gap-3 {
  gap: 16px;
}

.text-muted {
  color: #999;
  font-size: 14px;
}
.view-link {
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
}
.view-link:hover {
  text-decoration: underline;
}
/* Modern Modal Styling */
/* Modal Backdrop/Overlay with transparent shadow */
.modern-modal ::v-deep .modal {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.modern-modal ::v-deep .modal.show {
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.modern-modal ::v-deep .modal-dialog {
  max-width: 1000px;
  width: calc(100% - 40px);
  margin: 2rem auto;
  border-radius: 16px;
}

@media (min-width: 1024px) {
  .modern-modal ::v-deep .modal-dialog {
    width: 1000px;
  }
}

.modern-modal ::v-deep .modal-content {
  width: 100%;
  border: none;
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
}

.modern-modal ::v-deep .modal-header {
  background: linear-gradient(135deg, #FE672C 0%, #ff8a5c 100%);
  color: white;
  padding: 24px 32px;
  border-bottom: none;
  border-radius: 16px 16px 0 0;
}

.modern-modal ::v-deep .modal-header h3,
.modern-modal .modal-title {
  margin: 0;
  color: white;
  font-weight: 600;
  font-size: 22px;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.modal-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  backdrop-filter: blur(10px);
}

.modal-header-icon.icon-users {
  background: rgba(255, 255, 255, 0.25);
}

.modal-header-icon.icon-companies {
  background: rgba(255, 255, 255, 0.25);
}

.modal-header-icon.icon-emails {
  background: rgba(255, 255, 255, 0.25);
}

.modal-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  opacity: 0.9;
  font-weight: 400;
}

.selected-items-content {
  padding: 0;
  max-height: 65vh;
  overflow-y: auto;
  overflow-x: hidden;
  background: #fafbfc;
}

/* Modern Loading State */
.modern-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  min-height: 300px;
}

.loading-spinner {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FE672C 0%, #ff8a5c 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  animation: pulse 2s ease-in-out infinite;
}

.loading-spinner i {
  font-size: 28px;
  color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.9; }
}

.loading-text {
  color: #666;
  font-size: 15px;
  font-weight: 500;
  margin: 0;
}

/* Table Container */
.table-container {
  padding: 24px;
  background: white;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
}

.table-container ::v-deep .el-table {
  width: 100% !important;
  table-layout: fixed;
}

/* Modern Table Styling */
.modern-table ::v-deep .el-table {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e8eaed;
  width: 100% !important;
}

.modern-table ::v-deep .el-table__body-wrapper,
.modern-table ::v-deep .el-table__header-wrapper {
  overflow-x: hidden !important;
  overflow-y: auto;
}

.modern-table ::v-deep .el-table__body,
.modern-table ::v-deep .el-table__header {
  width: 100% !important;
}

.modern-table ::v-deep .el-table__header {
  background: #f8f9fa;
}

.modern-table ::v-deep .el-table__header th {
  background: #f8f9fa !important;
  color: #444C57;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 12px;
  border-bottom: 2px solid #e8eaed;
}

.modern-table ::v-deep .el-table__body tr {
  transition: all 0.2s ease;
}

.modern-table ::v-deep .el-table__body tr:hover {
  background: #f8f9fa !important;
  transform: translateX(2px);
}

.modern-table ::v-deep .el-table__body td {
  padding: 16px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.modern-table ::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background: #fafbfc;
}

.modern-table ::v-deep .el-table__empty-block {
  padding: 60px 20px;
}

.modern-table ::v-deep .el-table__empty-text {
  color: #999;
  font-size: 14px;
}

/* Table Cell Styling */
.table-cell-name,
.table-cell-email {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.table-text {
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  display: inline-block;
  color: #444C57;
  font-size: 14px;
}

.table-icon {
  color: #FE672C;
  font-size: 16px;
  opacity: 0.7;
  flex-shrink: 0;
}

/* Tag Styling - Professional Design */
.role-tag,
.status-tag,
.type-tag {
  font-weight: 600;
  padding: 1px 14px;
  border-radius: 8px;
  font-size: 12px;
  letter-spacing: 0.3px;
  border: none;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
}

/* Role Tags - Distinct Colors */
.role-tag.el-tag--danger {
  background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
  color: #fff;
  border: none;
}

.role-tag.el-tag--warning {
  background: linear-gradient(135deg, #f6ad55 0%, #ed8936 100%);
  color: #fff;
  border: none;
}

.role-tag.el-tag--success {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: #fff;
  border: none;
}

.role-tag.el-tag--info {
  background: linear-gradient(135deg, #63b3ed 0%, #4299e1 100%);
  color: #fff;
  border: none;
}

/* Status Tags - Clean and Modern */
.status-tag.el-tag--success {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 6px rgba(72, 187, 120, 0.25);
}

.status-tag.el-tag--info {
  background: linear-gradient(135deg, #a0aec0 0%, #718096 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 6px rgba(160, 174, 192, 0.25);
}

/* Type Tags */
.type-tag.el-tag--primary {
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 6px rgba(66, 153, 225, 0.25);
}

.type-tag.el-tag--info {
  background: linear-gradient(135deg, #63b3ed 0%, #4299e1 100%);
  color: #fff;
  border: none;
  box-shadow: 0 2px 6px rgba(99, 179, 237, 0.25);
}

.type-tag i {
  margin-right: 6px;
  font-size: 13px;
}

/* Hover effects for better interactivity */
.role-tag:hover,
.status-tag:hover,
.type-tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

/* Email List Container */
.email-list-container {
  padding: 24px;
  background: white;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;
}

.email-list-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
  font-weight: 600;
  color: #444C57;
  font-size: 16px;
}

.email-list-header i {
  color: #FE672C;
  font-size: 20px;
}

.email-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 0;
}

.email-tag-modern {
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  background: #f0f9ff;
  border: 1px solid #e0f2fe;
  color: #444C57;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.email-tag-modern:hover {
  background: #e0f2fe;
  border-color: #FE672C;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(254, 103, 44, 0.15);
}

.email-icon {
  color: #FE672C;
  font-size: 14px;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: center;
  padding: 24px;
  background: white;
}

.modern-pagination ::v-deep .el-pagination {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modern-pagination ::v-deep .el-pagination .el-pager li {
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.modern-pagination ::v-deep .el-pagination .el-pager li.active {
  background: #FE672C;
  color: white;
}

.modern-pagination ::v-deep .el-pagination .btn-prev,
.modern-pagination ::v-deep .el-pagination .btn-next {
  border-radius: 6px;
  transition: all 0.2s ease;
}

.modern-pagination ::v-deep .el-pagination .btn-prev:hover,
.modern-pagination ::v-deep .el-pagination .btn-next:hover {
  background: #FE672C;
  color: white;
}

/* Modal Footer */
.modern-modal ::v-deep .modal-footer {
  padding: 20px 32px;
  background: #fafbfc;
  border-top: 1px solid #e8eaed;
  border-radius: 0 0 16px 16px;
  display: flex;
  justify-content: flex-end;
}

.close-btn {
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f0f0f0;
  border-color: #d0d0d0;
}

/* Responsive */
@media (max-width: 768px) {
  .modern-modal ::v-deep .modal-dialog {
    max-width: calc(100% - 20px);
    width: calc(100% - 20px);
    margin: 1rem auto;
  }
  
  .modern-modal ::v-deep .modal-content {
    overflow-x: hidden;
  }
  
  .modal-header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .modal-header-icon {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }
  
  .selected-items-content {
    max-height: 70vh;
  }
  
  .table-container,
  .email-list-container {
    padding: 16px;
  }
  
  .modern-table ::v-deep .el-table__body-wrapper {
    overflow-x: scroll !important;
  }
}
@media (max-width: 768px) {
  .email-preview-modal ::v-deep .modal-dialog,
  .selected-items-modal ::v-deep .modal-dialog {
    max-width: 95vw;
    width: 95vw;
    margin: 1rem auto;
  }
}

/* Scoped modal-dialog style */
::v-deep .modal-dialog {
  max-width: 1000px !important;
}

/* New Recipient Selection Styles */
.recipient-selection-container {
  margin-bottom: 32px;
}

.selection-header {
  margin-bottom: 20px;
}

.selection-help-text {
  color: #666;
  font-size: 14px;
  margin: 0;
  font-style: italic;
}

.filter-category {
  margin-bottom: 32px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.filter-category:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.category-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
}

.category-icon {
  font-size: 24px;
  color: #FE672C;
  background: #fff5f0;
  padding: 10px;
  border-radius: 8px;
}

.category-title {
  font-size: 18px;
  font-weight: 600;
  color: #444C57;
  margin: 0;
  flex: 1;
}

.category-badge {
  background: #f0f9ff;
  color: #4299e1;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.filter-options-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.filter-option-card {
  background: #fafbfc;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  padding: 14px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.filter-option-card:hover:not(.disabled) {
  border-color: #FE672C;
  background: #fff5f0;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(254, 103, 44, 0.15);
}

.filter-option-card.selected {
  border-color: #FE672C;
  background: #fff5f0;
  box-shadow: 0 2px 8px rgba(254, 103, 44, 0.2);
}

.filter-option-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #f5f5f5;
}

.filter-card-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-card-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-weight: 500;
  color: #444C57;
  font-size: 14px;
}

.filter-count {
  font-size: 12px;
  color: #999;
  font-weight: 400;
}

.filter-count-skeleton {
  font-size: 12px;
  color: #FE672C;
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.filter-count-skeleton i {
  animation: spin 1s linear infinite;
}

.filter-warning-icon {
  color: #f56c6c;
  font-size: 18px;
  cursor: help;
}

.validation-error-banner {
  background: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #f56c6c;
  margin-top: 16px;
}

.validation-error-banner i {
  font-size: 18px;
}

/* Additional Selections Styles */
.additional-selections {
  margin-bottom: 24px;
}

.selection-field-card {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  transition: all 0.3s ease;
}

.selection-field-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.field-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.field-icon {
  font-size: 20px;
  color: #FE672C;
}

.field-label {
  font-weight: 600;
  color: #444C57;
  font-size: 15px;
  margin: 0;
}

.field-help-text {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
  margin-bottom: 0;
}

.company-filters-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
}

.company-filters-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #444C57;
  font-size: 14px;
  margin-bottom: 8px;
}

.company-filters-help {
  font-size: 12px;
  color: #666;
  margin-bottom: 12px;
  margin-top: 0;
}

.field-icon-small {
  font-size: 16px;
  color: #FE672C;
}

.company-filters {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.company-filters ::v-deep .el-checkbox {
  margin-right: 0;
}

.checkbox-label {
  font-size: 14px;
  color: #444C57;
  margin-left: 8px;
}

.role-filter-tabs {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.role-filter-label {
  font-size: 13px;
  font-weight: 500;
  color: #444C57;
}

.selected-users-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #67c23a;
}

.selected-users-summary .el-icon-check {
  font-size: 14px;
}

.company-users-picker .company-users-select {
  width: 100%;
}

.mb-2 { margin-bottom: 8px; }
.mb-3 { margin-bottom: 12px; }
.mt-2 { margin-top: 8px; }
.mt-3 { margin-top: 12px; }
.ml-2 { margin-left: 8px; }

/* Course Selection Card */
.course-selection-card {
  background: linear-gradient(135deg, #fff5f0 0%, #ffffff 100%);
  border: 2px solid #ffe5d6;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.course-selection-card:hover {
  box-shadow: 0 4px 16px rgba(254, 103, 44, 0.1);
  border-color: #FE672C;
}

.course-card-header {
  margin-bottom: 20px;
}

.course-header-left {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.course-icon {
  font-size: 28px;
  color: #FE672C;
  background: #fff;
  padding: 12px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(254, 103, 44, 0.15);
}

.course-card-title {
  font-size: 18px;
  font-weight: 600;
  color: #444C57;
  margin: 0 0 4px 0;
}

.course-card-subtitle {
  font-size: 13px;
  color: #666;
  margin: 0;
}

.course-fields-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 768px) {
  .course-fields-row {
    grid-template-columns: 1.5fr 1fr;
  }
}

.course-field,
.certificate-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.course-field-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #444C57;
  font-size: 14px;
}

.course-field-label i {
  color: #FE672C;
  font-size: 16px;
}

.optional-badge {
  background: #e6f7ff;
  color: #1890ff;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  margin-left: 8px;
}

/* Recipient Summary Card */
.recipient-summary-card {
  background: linear-gradient(135deg, #e6f7ff 0%, #f0f9ff 100%);
  border: 2px solid #bae7ff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.summary-icon {
  font-size: 24px;
  color: #1890ff;
}

.summary-title {
  font-size: 16px;
  color: #444C57;
  margin: 0;
}

.summary-content {
  padding-left: 36px;
}

.summary-text {
  font-size: 15px;
  color: #666;
  margin: 0 0 12px 0;
  line-height: 1.6;
}

.summary-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.view-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: #fff;
  border: 1px solid #1890ff;
  border-radius: 6px;
  color: #1890ff;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.view-link-btn:hover {
  background: #1890ff;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(24, 144, 255, 0.2);
}

.view-link-btn i {
  font-size: 14px;
}

.summary-note {
  font-size: 12px;
  color: #666;
  margin-top: 10px;
  margin-bottom: 0;
}

.summary-note i {
  margin-right: 4px;
}

/* Multi Filter Alert */
.multi-filter-alert {
  background: #fff7e6;
  border: 1px solid #ffe58f;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.multi-filter-alert i {
  font-size: 20px;
  color: #faad14;
  flex-shrink: 0;
}

.alert-content {
  flex: 1;
}

.alert-content strong {
  display: block;
  color: #444C57;
  margin-bottom: 6px;
  font-size: 14px;
}

.alert-content p {
  color: #666;
  font-size: 13px;
  margin: 4px 0 8px 0;
}

.selected-filters-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.filter-tag {
  background: #FE672C;
  color: #fff;
  border: none;
  font-weight: 500;
}

/*
 * Safe Dig Indiana branding (Marketing page)
 * Applied when VUE_APP_COMPANY_NAME_FRONTEND === 'safedig'.
 * Uses Safe Dig blue (#3e7799) for icons, filter-card hover/selected states, and accents.
 * Home-of-training keeps the default orange (#FE672C) styles above.
 */
.marketing-safedig .filter-option-card:hover:not(.disabled) {
  border-color: #3e7799;
  background: #e8f0f5;
  box-shadow: 0 4px 8px rgba(62, 119, 153, 0.15);
}
.marketing-safedig .filter-option-card.selected {
  border-color: #3e7799;
  background: #e8f0f5;
  box-shadow: 0 2px 8px rgba(62, 119, 153, 0.2);
}
.marketing-safedig .category-icon {
  color: #3e7799;
  background: #e8f0f5;
}
.marketing-safedig .field-icon,
.marketing-safedig .field-icon-small {
  color: #3e7799;
}
.marketing-safedig .course-icon {
  color: #3e7799;
  box-shadow: 0 2px 8px rgba(62, 119, 153, 0.2);
}
.marketing-safedig .table-icon {
  color: #3e7799;
}
.marketing-safedig .preview-icon {
  color: #3e7799;
  background: #e8f0f5;
}
.marketing-safedig .course-field-label i {
  color: #3e7799;
}
.marketing-safedig .filter-count-skeleton {
  color: #3e7799;
}
.marketing-safedig .summary-icon {
  color: #3e7799;
}
.marketing-safedig .category-header-small i {
  color: #3e7799;
  background: #e8f0f5;
}
.marketing-safedig .template-info {
  color: #3e7799;
}
.marketing-safedig .email-icon {
  color: #3e7799;
}
/* Section titles (Step 1, Step 2, Step 3 vertical bar) */
.marketing-safedig .section-title::before {
  background: #3e7799;
}
/* Course & Certificate Filters card */
.marketing-safedig .course-selection-card {
  background: linear-gradient(135deg, #e8f0f5 0%, #ffffff 100%);
  border-color: #b8d4e3;
}
.marketing-safedig .course-selection-card:hover {
  border-color: #3e7799;
  box-shadow: 0 4px 16px rgba(62, 119, 153, 0.1);
}
/* Placeholder tags (Step 3) */
.marketing-safedig .placeholder-tag:hover:not(.is-disabled) {
  border-color: #3e7799;
  background: #e8f0f5;
  box-shadow: 0 2px 8px rgba(62, 119, 153, 0.12);
}
.marketing-safedig .placeholder-value {
  color: #3e7799;
  background: #e8f0f5;
  border-color: #b8d4e3;
}
/* Show Example button & primary buttons */
.marketing-safedig .preview-help-toggle:hover {
  background: #e8f0f5;
  color: #3e7799;
}
.marketing-safedig ::v-deep .el-button--primary {
  background-color: #3e7799;
  border-color: #3e7799;
}
.marketing-safedig ::v-deep .el-button--primary:hover {
  background-color: #2d5a73;
  border-color: #2d5a73;
}
/* Show Example and other mini buttons */
.marketing-safedig ::v-deep .el-button--mini {
  background: #3e7799;
  border: 0;
  color: #fff;
}
.marketing-safedig ::v-deep .el-button--mini:hover {
  background: #2d5a73;
  color: #fff;
}
/* Tip box (Quick Preview) */
.marketing-safedig .preview-tip {
  background: #e8f0f5;
  border-left-color: #3e7799;
}
.marketing-safedig .preview-tip i {
  color: #3e7799;
}
/* Input/select focus ring */
.marketing-safedig ::v-deep .el-input__inner:focus,
.marketing-safedig ::v-deep .el-select .el-input__inner:focus {
  border-color: #3e7799 !important;
  box-shadow: 0 0 0 2px rgba(62, 119, 153, 0.2) !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .filter-options-grid {
    grid-template-columns: 1fr;
  }
  
  .course-fields-row {
    grid-template-columns: 1fr;
  }
  
  .course-header-left {
    flex-direction: column;
  }
  
  .summary-content {
    padding-left: 0;
  }
  
  .summary-actions {
    flex-direction: column;
  }
  
  .view-link-btn {
    width: 100%;
    justify-content: center;
  }
  
  .placeholder-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .placeholder-search-container {
    margin-bottom: 16px;
  }
  
  .placeholder-tag {
    padding: 10px 12px;
  }
  
  .placeholder-category {
    margin-bottom: 20px;
    padding-bottom: 16px;
  }
  
  .category-header-small {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .preview-content {
    padding-left: 0;
  }
  
  .preview-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .preview-example {
    padding: 12px;
  }
  
  ::v-deep .el-collapse-item__header {
    padding: 14px 16px;
  }
  
  ::v-deep .el-collapse-item__content {
    padding: 16px;
  }
}
</style>